import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Avatar, Chip, Paper, Card, CardContent, CircularProgress, Alert, Tooltip, useTheme, Checkbox, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, IconButton, TextField, InputAdornment, Stack, FormControl, Select, MenuItem, Switch, FormControlLabel, Menu, ListItemIcon, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import GetAppIcon from '@mui/icons-material/GetApp';
import MapIcon from '@mui/icons-material/Map';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MailIcon from '@mui/icons-material/Mail';
import PhoneIcon from '@mui/icons-material/Phone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Warning as WarningIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import SendInvoiceButton from '../components/admin/SendInvoiceButton';
import NotifyCustomerButton from '../components/admin/NotifyCustomerButton';
import PaymentStateChip from '../components/admin/PaymentStateChip';
import { JOB_TYPES, SERVICE_TYPES, JOB_STATUSES, formatDuration, durationChoices, payoutShownFor } from '../constants/jobOptions';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import { useJsApiLoader } from '@react-google-maps/api';
import { startOfWeek, format } from 'date-fns';
import { convertEventsTimesToCityTimeForCalendar, convertUTCToCityTimeForCalendar, getTimezoneForCity, convertCityTimeToUTC, formatCityTime } from '../utils/timezoneUtils';
import { checkMaidBookingConflict, getTimeSlot, getDateString, getConflictMessage, isMaidAvailableForSlot } from '../utils/timeSlotUtils';
import jobService from '../services/jobService';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import JobLineItemsEditor, { extrasTotal, extrasValid, fromApiLineItems, toApiLineItems } from '../components/admin/JobLineItemsEditor';
import { JobDetailsSection, InfoRow } from '../components/admin/JobDetailsSection';
import PeriodFilter from '../components/PeriodFilter';
import { getPeriodRange, isWithinRange } from '../utils/periodRange';

/**
 * ServiceSchedulePage - Manages service scheduling with timezone-aware display
 * 
 * DATA SOURCE:
 * - Uses new /api/jobs/getJobs endpoint to fetch jobs from local database
 * - Replaces external job sources with local database queries
 * - Provides comprehensive job data including maid assignments, tasks, and customer info
 * 
 * MAID DATA OPTIMIZATION:
 * - Uses /api/maids/light endpoint for lightweight maid data retrieval
 * - Returns only essential fields (id, firstName, lastName, userId, etc.) instead of full profile data
 * - Significantly improves performance by reducing data transfer and processing overhead
 * 
 * TIMEZONE HANDLING:
 * - Events are fetched from server in UTC format
 * - Events are converted to city timezone (MCO = Eastern Time) for storage and display
 * - FullCalendar receives events in city timezone and displays them correctly
 * - Table view formats city timezone dates directly using formatCityTimezoneDate()
 * - When saving changes, city timezone times are converted back to UTC for server
 */

const MaidProfileModal = ({ maid, open, onClose }) => {
  if (!maid) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Technician Profile</DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ width: 64, height: 64 }}>{maid.name?.[0] || maid.user?.firstName?.[0]}</Avatar>
          <Box>
            <Typography variant="h6">{maid.name || (maid.user?.firstName + ' ' + maid.user?.lastName)}</Typography>
            <Typography>Rating: {maid.rating} ({maid.score !== undefined ? `Score: ${Math.round(maid.score)}` : ''})</Typography>
            <Typography>Rate: {maid.rate || `$${maid.hourlyRate}/hr`}</Typography>
            <Typography>Experience: {maid.experience || 0} year{(maid.experience || 0) === 1 ? '' : 's'}</Typography>
            <Typography>Brings Supplies: {maid.bringsSupplies || maid.hasSupplies ? 'Yes' : 'No'}</Typography>
            <Typography>Available: {maid.available !== undefined ? (maid.available ? 'Yes' : 'No') : 'N/A'}</Typography>
            {typeof maid.minDistance === 'number' && !isNaN(maid.minDistance) ? (
              <Typography>Distance to client: {maid.minDistance.toFixed(2)} km</Typography>
            ) : null}
          </Box>
        </Box>
        <Typography variant="subtitle1">Skills:</Typography>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {maid.skills?.map(skill => <Chip key={skill} label={skill} />)}
        </Box>
        <Typography variant="subtitle1">Service Areas:</Typography>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {(maid.serviceAreas || []).map(area => <Chip key={area} label={area} color="info" />)}
        </Box>
        {/* Add more details as needed */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


// Fallback test data for resources and events
const fallbackResources = [
  { id: 'maid1', title: 'Alice Smith' },
  { id: 'maid2', title: 'Bob Johnson' },
  { id: 'maid3', title: 'Carmen Díaz' }
];
const fallbackEvents = [
  {
    id: 'booking1',
    resourceId: 'maid1',
    title: 'Cleaning @Downtown',
    start: '2025-06-16T09:00:00',
    end: '2025-06-16T12:00:00',
    backgroundColor: '#1976d2',
    extendedProps: {
      tooltip: 'Carlos Martinez, 404 Universal Blvd, Condo, 3h'
    }
  },
  {
    id: 'booking2',
    resourceId: 'maid2',
    title: 'Deep Clean @Baldwin',
    start: '2025-06-17T08:00:00',
    end: '2025-06-17T11:00:00',
    backgroundColor: '#388e3c',
    extendedProps: {
      tooltip: 'Jane Doe, 123 Main St, Deep Clean, 3h'
    }
  },
  {
    id: 'booking3',
    resourceId: 'maid3',
    title: 'Pet-Friendly @Dr. Phillips',
    start: '2025-06-18T10:00:00',
    end: '2025-06-18T12:00:00',
    backgroundColor: '#fbc02d',
    extendedProps: {
      tooltip: 'Carmen Díaz, 789 Oak Ave, Pet-Friendly, 2h'
    }
  }
];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Visible hour range of the timeGrid views (must match slotMinTime/slotMaxTime
// on the FullCalendar element). Events starting outside this window are
// clipped from week/day grids — the clipped-events hint uses these too.
const SLOT_MIN_HOUR = 6;
const SLOT_MAX_HOUR = 20;

const slotLabels = {
  early_morning: '6-9am',
  morning: '9am-12pm',
  afternoon: '12-5pm',
  evening: '5-8pm'
};

const WeeklyMaidAvailabilityGrid = ({ grid, slotDefs }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <Box ml={2}>
      <Box display="flex" flexDirection="column" gap={0.2}>
        {/* Header row */}
        <Box display="flex" gap={0.2}>
          <Box sx={{ width: 40 }} />
          {days.map(day => (
            <Box key={day} sx={{ width: 24, textAlign: 'center', fontSize: 12 }}>{day}</Box>
          ))}
        </Box>
        {slotDefs.map((slot, rowIdx) => (
          <Box key={slot.key} display="flex" gap={0.2} alignItems="center">
            <Box sx={{ width: 40, fontSize: 12 }}>{slotLabels[slot.key] || slot.key}</Box>
            {grid && grid.length > 0 && grid.map((day, colIdx) => (
              <Tooltip key={colIdx} title={`${days[colIdx]} ${slotLabels[slot.key]}`}> 
                <Box sx={{ width: 16, height: 16, borderRadius: '2px', bgcolor: day[rowIdx] ? '#388e3c' : '#d32f2f', border: '1px solid #888' }} />
              </Tooltip>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Technician picker: how many cards to render at a time
const MAID_LIST_PAGE_SIZE = 50;

// Kanban board columns for the admin "Board" view (driven by job status)
const BOARD_COLUMNS = [
  { id: 'NOT_STARTED', title: 'Upcoming' },
  { id: 'IN_PROGRESS', title: 'In Progress' },
  { id: 'COMPLETED', title: 'Done' },
  { id: 'CANCELLED', title: 'Cancelled' }
];

const boardColumnForStatus = (status) => {
  switch (status) {
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'NOT_STARTED';
  }
};

const ServiceSchedulePage = () => {

  // Helper functions for formatting job data
  const formatJobAddress = (address) => {
    if (!address) return 'Address not available';
    
    // If address is already a formatted string, return it
    if (typeof address === 'string') {
      return address;
    }
    
    // Handle different address field formats (HCP vs local)
    const parts = [
      address.street || address.street_address || address.address_line_1 || address.streetAddress,
      address.street_line_2 || address.address_line_2 || address.streetLine2,
      address.city,
      address.state || address.state_province || address.stateProvince,
      address.zip || address.zip_code || address.postal_code || address.zipCode || address.postalCode
    ].filter(Boolean);
    
    // Also try to extract from nested structures if it exists
    if (parts.length === 0 && address.address) {
      const nestedAddress = address.address;
      const nestedParts = [
        nestedAddress.street || nestedAddress.street_address || nestedAddress.address_line_1,
        nestedAddress.street_line_2 || nestedAddress.address_line_2,
        nestedAddress.city,
        nestedAddress.state || nestedAddress.state_province,
        nestedAddress.zip || nestedAddress.zip_code || nestedAddress.postal_code
      ].filter(Boolean);
      
      if (nestedParts.length > 0) {
        return nestedParts.join(', ');
      }
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Address format not recognized';
  };

  

  // Turns a job from GET /api/jobs/getJobs into a FullCalendar event (times in UTC;
  // convert with convertEventsTimesToCityTimeForCalendar before storing)
  const jobToCalendarEvent = (job) => {
    // Handle different date formats from local database
    let startDate, endDate;

    try {
      // Try to parse utcScheduledDate - could be string or Date object
      if (job.utcScheduledDate) {
        startDate = new Date(job.utcScheduledDate);
      } else if (job.utcScheduledTime) {
        // If no utcScheduledDate, try to construct from utcScheduledTime
        startDate = new Date(job.utcScheduledTime);
      } else {
        // Fallback to current date
        startDate = new Date();
      }

      // Calculate end date based on estimated duration
      const durationMinutes = job.estimatedDuration || 120; // Default to 2 hours
      endDate = new Date(startDate.getTime() + (durationMinutes * 60000));

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.warn('Invalid date for job:', job.id, job.utcScheduledDate, job.utcScheduledTime);
        startDate = new Date();
        endDate = new Date(startDate.getTime() + (durationMinutes * 60000));
      }
    } catch (dateError) {
      console.warn('Error parsing dates for job:', job.id, dateError);
      startDate = new Date();
      endDate = new Date(startDate.getTime() + 120 * 60000);
    }

    // Extract customer information from job.customer or fallback to HCP raw data
    let customerName = job.customer?.customerName || 'Unknown Customer';
    let customerEmail = job.customer?.customerEmail || null;
    let customerPhone = job.customer?.customerPhone || null;

    // If no customer info in main field, try to extract from HCP raw data
    if (!customerName || customerName === 'Unknown Customer') {
      if (job.hcpRawData?.customer?.name) {
        customerName = job.hcpRawData.customer.name;
      } else if (job.hcpRawData?.customer?.first_name || job.hcpRawData?.customer?.last_name) {
        customerName = `${job.hcpRawData.customer.first_name || ''} ${job.hcpRawData.customer.last_name || ''}`.trim();
      }
    }

    if (!customerEmail && job.hcpRawData?.customer?.email) {
      customerEmail = job.hcpRawData.customer.email;
    }

    if (!customerPhone && job.hcpRawData?.customer?.phone) {
      customerPhone = job.hcpRawData.customer.phone;
    }

    // Extract address information from job.address or fallback to HCP raw data
    let address = job.address || null;

    // If no address in main field, try to extract from HCP raw data
    if (!address && job.hcpRawData?.customer?.address) {
      const hcpAddress = job.hcpRawData.customer.address;
      address = {
        street: hcpAddress.street || hcpAddress.address_line_1,
        street_line_2: hcpAddress.street_line_2 || hcpAddress.address_line_2,
        city: hcpAddress.city,
        state: hcpAddress.state || hcpAddress.state_province,
        zip: hcpAddress.zip || hcpAddress.zip_code || hcpAddress.postal_code
      };
    }



    // Format address for display - ensure we never store empty objects
    const formattedAddress = address && typeof address === 'object' && Object.keys(address).length > 0 
      ? address 
      : null;

    // Extract maid assignments from the new structure
    const leadMaid = job.maidAssignments?.leadMaid || 
                    job.assignments?.find(a => a.role === 'LEAD')?.maid;

    const resourceId = leadMaid?.id || leadMaid?.userId || null;

    // Get job amount directly from job.totalAmount
    const amount = job.totalAmount || 0;

    // Get service description from job data
    const serviceDescription = job.specialInstructions || job.jobType || 'Cleaning Service';

    // Check if this is an HCP job and extract additional info
    const isHcpJob = job.sourceSystem === 'HCP';
    const hcpJobInfo = isHcpJob ? {
      hcpJobId: job.hcpJobId,
      hcpJobNumber: job.hcpJobNumber,
      importedAt: job.importedAt,
      sourceSystem: job.sourceSystem,
      invoiceNumber: job.invoiceNumber,
      workStatus: job.workStatus,
      outstandingBalance: job.outstandingBalance,
      hcpAssignedEmployees: job.hcpAssignedEmployees,
      hcpCompanyName: job.hcpCompanyName,
      hcpLeadSource: job.hcpLeadSource,
      arrivalWindow: job.arrivalWindow
    } : null;

    // Determine color based on assignment status first, then job status
    const hasAssignments = job.assignments && job.assignments.length > 0;
    const eventColor = hasAssignments 
      ? jobService.getJobStatusColor(job.status)
      : '#F44336'; // Red for unassigned jobs

    // Create tooltip text showing job address and assigned maids
    let addressText = 'Address not available';

    // Try multiple sources for address data (same priority order as modal)
    const jobAddress = job?.address;
    const hcpAddress = job?.hcpRawData?.customer?.address;
    const hcpAddressFromAddress = job?.hcpRawData?.address;

    // Try job address first
    if (jobAddress) {
      addressText = formatJobAddress(jobAddress);
    }
    // Try HCP raw data customer address
    else if (hcpAddress) {
      addressText = formatJobAddress(hcpAddress);
    }
    // Try HCP raw data direct address
    else if (hcpAddressFromAddress) {
      addressText = formatJobAddress(hcpAddressFromAddress);
    }
    // Note: bookingAddress is not available in this context (job mapping), 
    // but we follow the same priority order for other sources

    // Extract assigned maid names
    let maidNames = [];
    if (job.assignments && job.assignments.length > 0) {
      maidNames = job.assignments.map(assignment => {
        const maid = assignment.maid;
        if (maid && maid.firstName) {
          const fullName = `${maid.firstName} ${maid.lastName || ''}`.trim();
          const role = assignment.role === 'LEAD' ? '(Lead)' : assignment.role === 'ASSISTANT' ? '(Assistant)' : '';
          return role ? `${fullName} ${role}` : fullName;
        }
        return null;
      }).filter(Boolean);
    }

    // Combine address and maid information for tooltip
    let tooltipText = addressText;
    if (maidNames.length > 0) {
      tooltipText += `\nAssigned: ${maidNames.join(', ')}`;
    } else {
      tooltipText += '\nNo technicians assigned';
    }

    return {
      id: job.id,
      title: customerName,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      backgroundColor: eventColor,
      borderColor: eventColor,
      textColor: '#FFFFFF',
      resourceId: resourceId,
                    extendedProps: {
          tooltip: tooltipText, // Custom property for our tooltip
          job: {
            ...job,
            // Ensure maidAssignments is properly structured
            maidAssignments: job.maidAssignments || {
              leadMaid: leadMaid,
              assistants: job.assignments?.filter(a => a.role === 'ASSISTANT').map(a => a.maid) || [],
              allMaids: job.assignments?.map(a => a.maid) || []
            },
            // Include HCP-specific data
            hcpJobInfo: hcpJobInfo,
            // Include customer and address data for easy access
            customer: {
              id: job.customer?.id,
              name: customerName,
              email: customerEmail,
              phone: customerPhone
            },
            address: formattedAddress
          },
          // Keep booking object for backward compatibility with calendar and table views
          booking: {
            id: job.id,
            clientId: job.customer?.id,
            clientName: customerName,
            clientEmail: customerEmail,
            clientPhone: customerPhone,
            address: formattedAddress,
            status: job.status,
            amount: amount,
            specialInstructions: serviceDescription,
            mainService: serviceDescription,
            zipCode: formattedAddress?.zip || formattedAddress?.zipCode || null,
            companyName: job.hcpCompanyName
          },
        // Add HCP-specific data for easy access
        hcp: hcpJobInfo
      }
    };
  };

  const formatCurrency = (amountInCents) => {
    if (!amountInCents) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amountInCents / 100);
  };

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileMaid, setProfileMaid] = useState(null);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [matchedBookingId, setMatchedBookingId] = useState(null);
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maidAvailabilities, setMaidAvailabilities] = useState({});
  const [weeklyAvailGrid, setWeeklyAvailGrid] = useState({});
  const [weeklyAvailSlotDefs, setWeeklyAvailSlotDefs] = useState([]);
  const [weeklyAvailLoading, setWeeklyAvailLoading] = useState(false);
  const [weeklyAvailError, setWeeklyAvailError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [maidMatches, setMaidMatches] = useState([]);
  const [maidMatchLoading, setMaidMatchLoading] = useState(false);
  const [selectedMaids, setSelectedMaids] = useState([]);
  // Payout per selected technician in dollars, as typed ({ [selectionId]: '70' })
  const [teamPayouts, setTeamPayouts] = useState({});
  const [assigning, setAssigning] = useState(false);
  const [editingAssignments, setEditingAssignments] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState({ open: false, maidId: null, maidName: '' });
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
  const [confirmDeleteJob, setConfirmDeleteJob] = useState({ open: false, jobId: null, jobTitle: '' });
  const [editingJobDetails, setEditingJobDetails] = useState(false);
  const [jobEditForm, setJobEditForm] = useState(null);
  const [jobEditError, setJobEditError] = useState(null);
  const [savingJobDetails, setSavingJobDetails] = useState(false);
  // Add-on services being edited (jobEditForm.totalAmount is then the main service price)
  const [jobEditExtras, setJobEditExtras] = useState([]);
  // Bumped after a job edit to reload jobs quietly (no full-page spinner);
  // the ref names the job whose dialog should show the reloaded version.
  const [jobsReloadKey, setJobsReloadKey] = useState(0);
  const reselectJobIdRef = useRef(null);
  const [newTaskForm, setNewTaskForm] = useState({ title: '', category: '' });
  const [taskActionError, setTaskActionError] = useState(null);
  const [taskActionLoading, setTaskActionLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false); // Checklist of the open job is fetched on open
  const [notifyingTechnicians, setNotifyingTechnicians] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);
  const [notifyDialog, setNotifyDialog] = useState(null);
  const [teamPickerOpen, setTeamPickerOpen] = useState(false); // Job Details: technician picker shown in the side column
  const [jobMenuAnchor, setJobMenuAnchor] = useState(null); // Job Details: ⋯ menu // { recipients, note } while the "Notify technicians" dialog is open
  const [maidSearchQuery, setMaidSearchQuery] = useState('');
  const [maidListLimit, setMaidListLimit] = useState(MAID_LIST_PAGE_SIZE); // Picker renders the best matches first, more on demand
  const [dragFeedback, setDragFeedback] = useState(null);
  const [rescheduleConfirm, setRescheduleConfirm] = useState(null); // Pending calendar drag/resize awaiting confirmation
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'table', or 'board'
  const [boardPeriodType, setBoardPeriodType] = useState('week'); // 'week' | 'month'
  const [boardAnchor, setBoardAnchor] = useState(new Date());
  const [sortBy, setSortBy] = useState('scheduledStartTime');
  const [sortOrder, setSortOrder] = useState('asc');
  const [visibleEvents, setVisibleEvents] = useState([]); // Track events visible in current calendar view
  const [currentViewType, setCurrentViewType] = useState('timeGridTwoWeek'); // FullCalendar view type (timeGrid* views clip events outside slot hours)
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 }); // Tooltip state
  const [maidNameFilter, setMaidNameFilter] = useState(''); // Filter jobs by assigned maid name
  const [sourceSystemFilter, setSourceSystemFilter] = useState('all'); // Filter jobs by source system (all, smoobu, hcp, local)
  const [upcomingOnly, setUpcomingOnly] = useState(false); // Table view: hide jobs that have already ended
  const [allJobs, setAllJobs] = useState([]); // All jobs for conflict checking
  const [loadingJobs, setLoadingJobs] = useState(false); // Loading state for jobs
  const [syncingToHCP, setSyncingToHCP] = useState(false); // Syncing job to HCP state
  const [syncResult, setSyncResult] = useState(null); // Result of HCP sync (success/error)
  const [clientProfiles, setClientProfiles] = useState([]); // List of property manager client profiles
  const [loadingClients, setLoadingClients] = useState(false); // Loading client profiles
  const [linkingClient, setLinkingClient] = useState(false); // Linking job to client in progress
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '/api';
  const apiBaseUrl = apiBase; // Use apiBaseUrl if that's the convention elsewhere
  const calendarRef = useRef(null); // Reference to the FullCalendar component
  const [currentDateRange, setCurrentDateRange] = useState(null); // Track current calendar date range

  // Filtered events for both calendar and table views
  const filteredEvents = useMemo(() => {
    if (!events || events.length === 0) return [];

    let filtered = events;

    // Filter by source system if not 'all'
    if (sourceSystemFilter !== 'all') {
      filtered = filtered.filter(event => {
        const job = event.extendedProps?.job;
        if (!job) return false;

        const sourceSystem = (job.sourceSystem || 'local').toLowerCase();

        if (sourceSystemFilter === 'smoobu') {
          return sourceSystem === 'smoobu';
        } else if (sourceSystemFilter === 'hcp') {
          return sourceSystem === 'hcp';
        } else if (sourceSystemFilter === 'local') {
          return sourceSystem === 'local' || !job.sourceSystem;
        }

        return true;
      });
    }

    // Filter events by maid name if filter is active
    if (maidNameFilter.trim()) {
      const searchTerm = maidNameFilter.toLowerCase().trim();
      filtered = filtered.filter(event => {
        const job = event.extendedProps?.job;
        if (!job || !job.assignments || job.assignments.length === 0) {
          return false; // No assignments, so no maid names to match
        }

        // Check if any assigned maid's name matches the search term
        return job.assignments.some(assignment => {
          const maid = assignment.maid;
          if (!maid) return false;

          const firstName = (maid.firstName || '').toLowerCase();
          const lastName = (maid.lastName || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();

          return firstName.includes(searchTerm) ||
                 lastName.includes(searchTerm) ||
                 fullName.includes(searchTerm);
        });
      });
    }

    return filtered;
  }, [events, maidNameFilter, sourceSystemFilter]);

  // Board view: period (week/month) range + events that fall within it
  const { start: boardRangeStart, end: boardRangeEnd } = useMemo(
    () => getPeriodRange(boardPeriodType, boardAnchor),
    [boardPeriodType, boardAnchor]
  );

  const boardEvents = useMemo(
    () => filteredEvents.filter((ev) => isWithinRange(ev.start, boardRangeStart, boardRangeEnd)),
    [filteredEvents, boardRangeStart, boardRangeEnd]
  );

  // Sorted and processed events for table view
  const sortedEvents = useMemo(() => {
    if (!filteredEvents || filteredEvents.length === 0) return [];

    let listEvents = filteredEvents;
    if (upcomingOnly) {
      // Event times are city-local wall-clock Dates, so compare against "now" in the same frame
      const cityNow = convertUTCToCityTimeForCalendar(new Date(), process.env.REACT_APP_CITY_CODE || 'ORL');
      listEvents = filteredEvents.filter((event) => new Date(event.end || event.start) >= cityNow);
    }

    return [...listEvents].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'customer':
          aVal = a.extendedProps?.booking?.clientName || '';
          bVal = b.extendedProps?.booking?.clientName || '';
          break;
        case 'service':
          aVal = a.extendedProps?.booking?.mainService || '';
          bVal = b.extendedProps?.booking?.mainService || '';
          break;
        case 'amount':
          aVal = a.extendedProps?.job?.totalAmount || 0;
          bVal = b.extendedProps?.job?.totalAmount || 0;
          break;
        case 'status':
          aVal = a.extendedProps?.booking?.status || '';
          bVal = b.extendedProps?.booking?.status || '';
          break;
        case 'scheduledStartTime':
        default:
          aVal = new Date(a.start);
          bVal = new Date(b.start);
          break;
      }
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [filteredEvents, sortBy, sortOrder, upcomingOnly]);

  // Memoized calculation of total job amount that updates based on current view and data
  const totalJobAmount = useMemo(() => {
    let total = 0;
    
    // Use the appropriate data source based on current view
    // For calendar view, use only visible events; for table view, use sorted events
    const dataSource = viewMode === 'table' ? sortedEvents :
                      viewMode === 'board' ? boardEvents :
                      (visibleEvents.length > 0 ? visibleEvents : filteredEvents);
    
    dataSource.forEach(event => {
      const job = event.extendedProps?.job;
      
      // Use only job.totalAmount field
      if (job?.totalAmount && job.totalAmount > 0) {
        total += job.totalAmount;
      }
    });
    
    return total;
  }, [filteredEvents, sortedEvents, visibleEvents, boardEvents, viewMode]); // Recalculate when visible events change

  // Filtered maid matches based on search query
  const filteredMaidMatches = useMemo(() => {
    if (!maidSearchQuery.trim()) {
      return maidMatches;
    }
    
    const searchTerm = maidSearchQuery.toLowerCase().trim();
    return maidMatches.filter(maid => {
      const firstName = maid.firstName || maid.user?.firstName || '';
      const lastName = maid.lastName || maid.user?.lastName || '';
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      
      return fullName.includes(searchTerm) || 
             firstName.toLowerCase().includes(searchTerm) || 
             lastName.toLowerCase().includes(searchTerm);
    });
  }, [maidMatches, maidSearchQuery]);

  // Rendering every technician card is what made the job dialog slow to type in
  // and save (hundreds of cards re-rendered on each change), so only the first
  // matches are shown, plus any already-selected technician further down
  const shownMaidMatches = useMemo(() => {
    const shown = filteredMaidMatches.length <= maidListLimit
      ? filteredMaidMatches
      : filteredMaidMatches.filter((maid, index) =>
        index < maidListLimit || selectedMaids.includes(maid.userId || maid.id || maid.user?.id));
    // The job's current technicians first, so "Change" opens on the team as it is
    const onJob = new Set((selectedEvent?.job?.assignments || []).map((a) => a.maidId || a.maid?.id).filter(Boolean));
    if (!onJob.size) return shown;
    const idOf = (maid) => maid.userId || maid.id || maid.user?.id;
    return [...shown.filter((m) => onJob.has(idOf(m))), ...shown.filter((m) => !onJob.has(idOf(m)))];
  }, [filteredMaidMatches, maidListLimit, selectedMaids, selectedEvent]);

  // Handle view mode change
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (!sortedEvents || sortedEvents.length === 0) {
      alert('No data to export');
      return;
    }

    // CSV headers
    const headers = [
      'Date',
      'Start Time', 
      'End Time',
      'Customer Name',
      'Customer Email',
      'Service Type',
      'Duration (min)',
      'Amount',
      'Status',
      'Assigned Team',
      'Address',
      'Special Instructions',
      'HCP Job ID',
      'Invoice Number',
      'HCP Work Status',
      'Company Name',
      'Source System'
    ];

    // Generate CSV rows
    const rows = sortedEvents.map(event => {
      const booking = event.extendedProps?.booking;
      const job = event.extendedProps?.job;

      // Format date and time using formatCityTime() for consistency
      const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
      
      const dateStr = formatCityTime(event.start, cityCode, { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const startTimeStr = formatCityTime(event.start, cityCode, { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      });
      const endTimeStr = event.end ? formatCityTime(event.end, cityCode, { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true 
      }) : 'N/A';

      // Extract customer info
      const customerName = booking?.clientName || 'N/A';
      const customerEmail = booking?.clientEmail || 'N/A';

      // Extract service info
      const serviceType = booking?.mainService || 'N/A';
      const duration = job?.estimatedDuration || 'N/A';

      // Format amount using only job.totalAmount
      let amount = 'N/A';
      if (job?.totalAmount) {
        amount = `$${(job.totalAmount / 100).toFixed(2)}`;
      }

      // Extract status
      const status = booking?.status || job?.status || 'Unknown';

      // Extract assigned team
      let assignedTeam = 'Not assigned';
      if (job?.assignments?.length > 0) {
        assignedTeam = job.assignments.map(assignment => 
          `${assignment.maid?.firstName} ${assignment.maid?.lastName} (${assignment.role})`
        ).join(', ');
      }

      // Extract address
      const address = booking?.address || job?.address || 'N/A';

      // Extract special instructions
      const instructions = job?.specialInstructions || booking?.specialInstructions || '';

      // Extract HCP-specific info
      const hcpJobInfo = job.hcpJobInfo || {};
      const hcpJobId = hcpJobInfo.hcpJobId || 'N/A';
      const invoiceNumber = hcpJobInfo.invoiceNumber || 'N/A';
      const workStatus = hcpJobInfo.workStatus || 'N/A';
      const companyName = hcpJobInfo.hcpCompanyName || 'N/A';
      const sourceSystem = hcpJobInfo.sourceSystem || 'Local';

      return [
        dateStr,
        startTimeStr,
        endTimeStr,
        customerName,
        customerEmail,
        serviceType,
        duration,
        amount,
        status,
        assignedTeam,
        address,
        instructions,
        hcpJobId,
        invoiceNumber,
        workStatus,
        companyName,
        sourceSystem
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(field => {
        // Escape quotes and wrap in quotes if contains comma or quote
        const fieldStr = String(field || '');
        if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      }).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `service-schedule-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Handler for tracking visible events when calendar view changes
  const handleDatesSet = (dateInfo) => {
    // Get the start and end dates of the current view
    const viewStart = dateInfo.start;
    const viewEnd = dateInfo.end;

    // Save the current date range for use in useEffect
    setCurrentDateRange({ start: viewStart, end: viewEnd });
    setCurrentViewType(dateInfo.view?.type || 'timeGridTwoWeek');

    // Filter FILTERED events (not all events) that fall within the current view range
    // This ensures totalJobAmount only counts jobs visible in the current calendar view
    const eventsInView = filteredEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end || event.start);

      // Check if event overlaps with the current view range
      return (eventStart < viewEnd && eventEnd >= viewStart);
    });

    setVisibleEvents(eventsInView);
  };

  // Handler for showing tooltip on mouse enter
  const handleEventMouseEnter = (info) => {
    const event = info.event;
    const tooltipContent = event.extendedProps?.tooltip;
    
    if (tooltipContent) {
      setTooltip({
        show: true,
        content: tooltipContent,
        x: info.jsEvent.pageX + 10,
        y: info.jsEvent.pageY - 10
      });
    }
  };

  // Handler for hiding tooltip on mouse leave
  const handleEventMouseLeave = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  // Recalculate visible events when filteredEvents changes
  // This ensures that when filters (like maid name filter) are applied,
  // the totalJobAmount updates correctly without requiring a calendar view change
  useEffect(() => {
    if (!currentDateRange) {
      // No date range set yet (calendar hasn't initialized)
      return;
    }

    const { start: viewStart, end: viewEnd } = currentDateRange;

    // Filter the filtered events based on the current calendar date range
    const eventsInView = filteredEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end || event.start);

      // Check if event overlaps with the current view range
      return (eventStart < viewEnd && eventEnd >= viewStart);
    });

    setVisibleEvents(eventsInView);
  }, [filteredEvents, currentDateRange]);

  // Events in the current range whose start time falls outside the timeGrid's
  // visible hours (slotMinTime–slotMaxTime). These render fine in Month view
  // but are silently clipped from week/day grids — surface them as a hint.
  const clippedEvents = useMemo(() => {
    if (!currentViewType.startsWith('timeGrid')) return [];
    return visibleEvents.filter((event) => {
      const startHour = new Date(event.start).getHours();
      return startHour < SLOT_MIN_HOUR || startHour >= SLOT_MAX_HOUR;
    });
  }, [visibleEvents, currentViewType]);

  useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });


  // Consolidated data fetching on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      const quietReload = jobsReloadKey > 0;
      if (!quietReload) setLoading(true);
      setError(null);
      
      try {
        console.log('🔄 Fetching all calendar data...');
        
        // Fetch all data in parallel
        const [serviceCatalogRes, maidsRes] = await Promise.all([
          fetch(`${apiBase}/api/service-catalog?cityCode=MCO&tenantId=1`),
          fetch(`${apiBase}/api/maids/light?activeOnly=true`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
          })
        ]);

        // Process service catalog
        const serviceCatalogData = await serviceCatalogRes.json();
        setServiceCatalog(serviceCatalogData);

        // Process maids data from /maids/light endpoint
        const maidsData = await maidsRes.json();
        const maids = maidsData.data || maidsData; // /maids/light returns data directly or under data property
        console.log('👥 Fetched maids from /maids/light endpoint:', maids.length);
        console.log('🏠 Sample maid data structure:', maids[0]); // Debug: show the flattened structure
        
        // Map maids to resources (using the flattened structure from /maids/light)
        // Performance optimization: /maids/light returns only essential fields instead of full profile data
        const mappedResources = maids.map(maid => ({
          id: maid.userId || maid.id, // /maids/light returns userId directly
          title: `${maid.firstName} ${maid.lastName}`, // Names are flattened
        }));
        setResources(mappedResources);

        // Fetch jobs using jobService for consistency
        const jobsResponse = await jobService.fetchJobs({
          tenantId: 1,
          cityCode: 'MCO',
          limit: 300, // Increased from 100 to 300 to load more jobs
          page: 1, // Always start with page 1 on initial load
          lite: true // No checklists or raw HCP/Smoobu copies; tasks load when a job is opened
        });
        console.log('📅 Jobs response from getJobs endpoint:', jobsResponse);
        
        // Validate response structure
        if (!jobsResponse) {
          throw new Error('No response received from getJobs endpoint');
        }
        
        if (!jobsResponse.success && jobsResponse.error) {
          throw new Error(`getJobs endpoint error: ${jobsResponse.error}`);
        }
        
        // Extract jobs array from the new response structure
        let jobsArray = jobsResponse.data?.jobs || jobsResponse.jobs || jobsResponse || [];
        console.log('📅 Extracted jobs array:', jobsArray.length, 'jobs');
        
        if (!Array.isArray(jobsArray)) {
          console.warn('Jobs array is not an array, attempting to convert:', jobsArray);
          const fallbackArray = Array.isArray(jobsResponse) ? jobsResponse : [];
          console.log('📅 Using fallback array with', fallbackArray.length, 'jobs');
          jobsArray = fallbackArray;
        }
        
        if (jobsArray.length === 0) {
          console.log('📅 No jobs found in response, this might be normal for empty databases');
        }
        
        // Log sample job structure for debugging
        if (jobsArray.length > 0) {
          console.log('📅 Sample job structure from local database:', {
            id: jobsArray[0].id,
            status: jobsArray[0].status,
            jobType: jobsArray[0].jobType,
            utcScheduledDate: jobsArray[0].utcScheduledDate,
            utcScheduledTime: jobsArray[0].utcScheduledTime,
            estimatedDuration: jobsArray[0].estimatedDuration,
            totalAmount: jobsArray[0].totalAmount,
            customer: jobsArray[0].customer,
            booking: jobsArray[0].booking,
            address: jobsArray[0].address,
            maidAssignments: jobsArray[0].maidAssignments,
            assignments: jobsArray[0].assignments
          });
        }
        
        // Transform jobs to FullCalendar event format
        const calendarEvents = jobsArray.map(jobToCalendarEvent);
        
        console.log('📅 Transformed jobs to calendar events:', calendarEvents);
        
        // Log HCP-specific data for debugging
        const hcpJobs = calendarEvents.filter(event => event.extendedProps?.hcp);
        if (hcpJobs.length > 0) {
          console.log('🏢 HCP Jobs found with enhanced data:', hcpJobs.length);
          console.log('Sample HCP job data:', hcpJobs[0]?.extendedProps?.hcp);
        }
        
        // Convert events to city timezone for FullCalendar display
        const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
        const cityTimezoneEvents = convertEventsTimesToCityTimeForCalendar(calendarEvents, cityCode);
        
        console.log('🕐 Events converted to city timezone for calendar display:', cityTimezoneEvents);
        console.log('Sample converted event:', cityTimezoneEvents[0]);
        
        setEvents(cityTimezoneEvents); // Store events in city timezone for calendar
        if (quietReload) {
          // Swap in the reloaded events everywhere, and refresh an open job dialog
          const byId = new Map(cityTimezoneEvents.map((ev) => [ev.id, ev]));
          setVisibleEvents((prev) => prev.map((ev) => byId.get(ev.id) || ev));
          const reselectId = reselectJobIdRef.current;
          reselectJobIdRef.current = null;
          const updated = reselectId && cityTimezoneEvents.find(
            (ev) => String(ev.extendedProps?.job?.id || '').replace(/^job_/, '') === reselectId
          );
          if (updated) {
            setSelectedEvent((prev) => (prev ? { ...prev, booking: updated.extendedProps.booking, job: { ...updated.extendedProps.job, tasks: prev.job?.tasks } } : prev));
          }
        }
        setAllJobs(jobsArray); // Store all jobs for conflict checking
        
        console.log(`📊 Loaded ${jobsArray.length} jobs with increased limit of 300`);
        
        
      } catch (err) {
        console.error('❌ Error loading calendar data:', err);
        
        // Provide more specific error information for getJobs endpoint
        if (err.message && err.message.includes('getJobs')) {
          console.error('❌ getJobs endpoint specific error:', err.message);
          setError(`Failed to load jobs from local database: ${err.message}. Please check if the backend server is running and the getJobs endpoint is accessible.`);
        } else {
          setError('Failed to load calendar data. Please refresh the page.');
        }
        
        // Set fallback data only on error
        setResources(fallbackResources);
        setEvents(fallbackEvents);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, apiBaseUrl, jobsReloadKey]);

  // Update visible events when filtered events data changes
  useEffect(() => {
    // If we don't have visible events set yet, use all filtered events as initial visible events
    if (visibleEvents.length === 0 && filteredEvents.length > 0) {
      setVisibleEvents(filteredEvents);
    }
  }, [filteredEvents, visibleEvents.length]);

  // Extract jobs from events for conflict checking
  useEffect(() => {
    if (events && events.length > 0) {
      const jobsFromEvents = events
        .map(event => event.extendedProps?.job)
        .filter(job => job && job.id); // Only include valid jobs with IDs

      setAllJobs(jobsFromEvents);
    } else {
      setAllJobs([]);
    }
  }, [events]);

  // Fetch client profiles for property manager linking
  useEffect(() => {
    const fetchClientProfiles = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${apiBase}/api/users/clients`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        let clientsData = [];
        if (response.data && Array.isArray(response.data)) {
          clientsData = response.data;
        } else if (response.data.clients && Array.isArray(response.data.clients)) {
          clientsData = response.data.clients;
        }

        // Transform User objects to ClientProfile format for dropdown
        // API returns User objects with nested clientProfile, but we need ClientProfile objects with nested User
        const transformedClients = clientsData.map(client => {
          // Check if clientProfile exists (it should based on the API query)
          const clientProfileId = client.clientProfile?.id || client.id;

          return {
            id: clientProfileId,  // Use ClientProfile ID for linking jobs
            User: {
              id: client.id,
              firstName: client.firstName,
              lastName: client.lastName,
              email: client.email,
              phoneNumber: client.phoneNumber
            },
            address: client.address,
            city: client.city,
            state: client.state
          };
        });

        setClientProfiles(transformedClients);
      } catch (error) {
        console.error('Error fetching client profiles:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClientProfiles();
  }, [apiBase]);

  /* UNUSED fetchEvents function removed - it was not being called anywhere */

  const handleAssignMaid = async (request, maid) => {
    // Try to use bookingId if present, else create booking
    const bookingId = request.bookingId;
    let payload = {};
    let url = '';
    let newBookingId = bookingId;
    if (!bookingId) {
      // Compose booking creation payload from request and maid
      if (!request || !maid) {
        alert('Missing request or technician info');
        return;
      }
      // Estimate duration (fallback to 120 min if not available)
      const serviceDuration = 120;
      const serviceDate = request.preferredDate || new Date().toISOString().slice(0, 10);
      // Default to 9:00 AM if no preferredTime
      let serviceTime;
      if (request.preferredDate) {
        serviceTime = `${request.preferredDate}T${request.preferredTime || '09:00'}`;
      } else {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        serviceTime = `${yyyy}-${mm}-${dd}T09:00`;
      }
      // Always use maid.userId as maidId
      const maidId = maid.userId;
      if (!maidId) {
        alert('No valid technician user ID found!');
        return;
      }
      const bookingPayload = {
        maidId,
        clientId: request.customerId,
        serviceTime,
        serviceDate,
        serviceDuration,
        specialInstructions: request.specialInstructions || ''
      };
      // Create booking first
      const createRes = await fetch(`${apiBase}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(bookingPayload)
      });
      if (!createRes.ok) {
        alert('Failed to create booking');
        return;
      }
      const createdBooking = await createRes.json();
      newBookingId = createdBooking.id;
    }
    // Now call the match endpoint
    url = `${apiBase}/api/bookings/${newBookingId}/match`;
    payload = { bookingId: newBookingId };
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to mark booking as matched');
      const data = await res.json();
      setMatchedBookingId(data.booking.id);
      alert('Technician matched and handshake saved!');
    } catch (err) {
      alert(err.message);
    }
  };



  const renderEventContent = (eventInfo) => {
    const booking = eventInfo.event.extendedProps.booking;
    const job = eventInfo.event.extendedProps.job;

    if (!booking) return null;
    
    return (
      <span style={{ 
        display: 'block', 
        width: '100%', 
        height: '100%', 
        cursor: 'pointer',
        position: 'relative',
        borderRadius: '4px'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {booking.clientName || 'Unknown Customer'}
        </Typography>
        <Typography variant="caption" display="block">
          {booking.mainService || 'Unknown Service'}
        </Typography>
        {/* Display rental amount for Smoobu jobs */}
        {job?.sourceSystem === 'SMOOBU' ? (
          <>
            {(job?.rentalAmount || booking?.rentalAmount) && (
              <Typography variant="caption" display="block" sx={{ color: 'info.main', fontSize: '0.7rem' }}>
                Rental: {formatCurrency(job?.rentalAmount || booking?.rentalAmount)}
              </Typography>
            )}
            {job?.totalAmount && (
              <Typography variant="caption" display="block" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                Clean: {formatCurrency(job.totalAmount)}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" display="block" sx={{ color: 'success.main', fontWeight: 'bold' }}>
            {job?.totalAmount ? formatCurrency(job.totalAmount) : 'N/A'}
          </Typography>
        )}
        {/* Compact payment marker (full badge on cards and in the job dialog) */}
        {(job?.paymentState === 'PAID' || job?.paymentState === 'UNPAID') && (
          <Typography
            variant="caption"
            display="block"
            sx={{ fontWeight: 'bold', fontSize: '0.7rem', color: job.paymentState === 'PAID' ? 'success.dark' : 'warning.dark' }}
          >
            {job.paymentState === 'PAID' ? '✓ Paid' : 'Unpaid'}
          </Typography>
        )}
      </span>
    );
  };

  // --- Event Click Handler: Fetch Maid Matches ---
  const handleEventClick = (info) => {
    const booking = info.event.extendedProps.booking;
    const job = info.event.extendedProps.job;

    // Open the modal immediately — all job/booking data is already client-side.
    // Maid matches load in the background; the picker shows its own spinner.
    setMaidMatches([]);
    setEditingJobDetails(false);
    setJobEditForm(null);
    setJobEditError(null);
    setNewTaskForm({ title: '', category: '' });
    setTaskActionError(null);
    setSelectedEvent({ booking, job });
    setEventModalOpen(true);
    setEditingAssignments(false);
    setTeamPickerOpen(false);
    setSelectedMaids([]);
    setMaidSearchQuery('');
    setMaidListLimit(MAID_LIST_PAGE_SIZE);

    loadMaidMatches(job, booking);
    loadJobTasks(job);
  };

  // The schedule list is loaded without checklists; fetch the open job's tasks
  const tasksRequestRef = useRef(null);
  const loadJobTasks = async (job) => {
    const jobId = String(job?.id || '').replace(/^job_/, '');
    if (!jobId) return;
    const isSameJob = (prev) => prev && String(prev.job?.id || '').replace(/^job_/, '') === jobId;
    tasksRequestRef.current = jobId;
    setTasksLoading(true);
    try {
      const res = await axios.get(
        `${apiBase}/api/jobs/${jobId}/tasks`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setSelectedEvent((prev) => (isSameJob(prev) ? { ...prev, job: { ...prev.job, tasks: res.data || [] } } : prev));
    } catch (err) {
      console.error('Failed to load job tasks:', err);
    } finally {
      if (tasksRequestRef.current === jobId) setTasksLoading(false);
    }
  };

  // Re-fetch one job after an edit and swap it into every view, instead of
  // reloading all jobs. Falls back to a full reload if the fetch fails.
  const refreshJob = async (jobId) => {
    const isThisJob = (id) => String(id || '').replace(/^job_/, '') === jobId;
    try {
      const res = await jobService.fetchJobs({ tenantId: 1, cityCode: 'MCO', limit: 1, jobId, lite: true });
      const job = res?.data?.jobs?.[0] || null; // null: no longer listed (e.g. cancelled)
      const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
      const updated = job ? convertEventsTimesToCityTimeForCalendar([jobToCalendarEvent(job)], cityCode)[0] : null;

      const swap = (list) => (updated
        ? list.map((ev) => (isThisJob(ev.id) ? updated : ev))
        : list.filter((ev) => !isThisJob(ev.id)));
      setEvents(swap);
      setVisibleEvents(swap);
      setAllJobs((prev) => (job ? prev.map((j) => (isThisJob(j.id) ? job : j)) : prev.filter((j) => !isThisJob(j.id))));
      if (updated) {
        setSelectedEvent((prev) => (prev && isThisJob(prev.job?.id)
          ? { ...prev, booking: updated.extendedProps.booking, job: { ...updated.extendedProps.job, tasks: prev.job?.tasks } }
          : prev));
      }
    } catch (err) {
      console.error('Failed to refresh job, reloading all jobs:', err);
      reselectJobIdRef.current = jobId;
      setJobsReloadKey((k) => k + 1);
    }
  };

  // Background loader for the maid picker. Guarded so a slow response for a
  // previously clicked job can't overwrite the matches of the current one.
  const maidMatchRequestRef = useRef(null);
  const loadMaidMatches = async (job, booking) => {
    const requestId = job?.id || Symbol('match');
    maidMatchRequestRef.current = requestId;
    setMaidMatchLoading(true);

    const zipcode = job?.hcpRawData?.customer?.address?.zip ||
                    job?.hcpRawData?.address?.zip ||
                    booking?.zipCode ||
                    booking?.address?.zipCode ||
                    job?.address?.zipCode;

    let matches = [];
    try {
      const matchRes = await axios.get(
        `${apiBase}/api/maids/match?jobId=${job.id}&zipcode=${zipcode}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      matches = matchRes.data.maids || [];
    } catch (matchError) {
      console.log('Match endpoint failed, falling back to all maids:', matchError.message);
    }

    // If match returned no results, fall back to loading all active maids
    if (matches.length === 0) {
      try {
        const lightRes = await fetch(
          `${apiBase}/api/maids/light`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        const lightData = await lightRes.json();
        const allMaids = lightData.data || lightData || [];
        // Normalize to the same shape expected by the maid picker
        matches = allMaids.map(m => ({
          id: m.id,
          userId: m.userId || m.id,
          firstName: m.firstName,
          lastName: m.lastName,
          email: m.email,
          score: 0,
          _fallback: true
        }));
      } catch (fallbackError) {
        console.error('Fallback maid load also failed:', fallbackError);
      }
    }

    // Ignore stale responses from an earlier click
    if (maidMatchRequestRef.current !== requestId) return;

    setMaidMatches(matches);
    setMaidMatchLoading(false);
  };

  // Handler for selecting/deselecting maids
  const handleMaidSelect = (maidId) => {
    setSelectedMaids(prev =>
      prev.includes(maidId) ? prev.filter(id => id !== maidId) : [...prev, maidId]
    );
  };



  // Drag-and-drop / resize on the calendar: the event moves immediately and a
  // confirmation dialog asks who to email. Saving goes through PUT /api/jobs/:id
  // (which emails the technicians unless notifyTechnicians is false); the
  // customer email reuses the "Notify Customer" confirmation. Cancel reverts the move.
  const openRescheduleConfirm = (info, { includeDuration }) => {
    const { event, oldEvent } = info;
    const job = event.extendedProps?.job || {};
    const jobId = String(job.id || event.id).replace(/^job_/, '');
    if (!jobId || !event.start) {
      info.revert();
      return;
    }

    const technicians = (job.assignments || [])
      .filter((a) => a.status !== 'DECLINED' && a.maid)
      .map((a) => `${a.maid.firstName || ''} ${a.maid.lastName || ''}`.trim())
      .filter(Boolean);
    const customerEmail = job.customer?.email || job.clientProfile?.User?.email || null;

    // Same rules the server applies before emailing anyone about a visit
    const cityNow = convertUTCToCityTimeForCalendar(new Date(), process.env.REACT_APP_CITY_CODE || 'ORL');
    const notifyBlockedReason = ['COMPLETED', 'CANCELLED'].includes(job.status)
      ? `This job is ${job.status.toLowerCase()}.`
      : event.start < cityNow ? 'The new time is in the past.' : null;

    setTooltip({ show: false, content: '', x: 0, y: 0 }); // mouseleave doesn't fire during a drag
    const canNotifyTechnicians = !notifyBlockedReason && technicians.length > 0;
    const canNotifyCustomer = !notifyBlockedReason && technicians.length > 0 && Boolean(customerEmail);
    setRescheduleConfirm({
      info,
      jobId,
      includeDuration,
      customerName: event.extendedProps?.booking?.clientName || 'this job',
      fromStart: oldEvent.start,
      fromEnd: oldEvent.end,
      toStart: event.start,
      toEnd: event.end,
      technicians,
      customerEmail,
      notifyBlockedReason,
      canNotifyTechnicians,
      canNotifyCustomer,
      notifyTechnicians: canNotifyTechnicians,
      notifyCustomer: canNotifyCustomer,
      saving: false,
    });
  };

  const handleCancelReschedule = () => {
    if (rescheduleConfirm?.saving) return;
    rescheduleConfirm?.info.revert();
    setRescheduleConfirm(null);
  };

  const handleConfirmReschedule = async () => {
    const pending = rescheduleConfirm;
    if (!pending || pending.saving) return;
    const { jobId, toStart, toEnd, includeDuration } = pending;
    const notifyTechnicians = pending.canNotifyTechnicians && pending.notifyTechnicians;
    const notifyCustomer = pending.canNotifyCustomer && pending.notifyCustomer;

    // Calendar dates hold the city wall-clock time in local Date fields
    const pad = (n) => String(n).padStart(2, '0');
    const toWallClock = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
    const utcStart = convertCityTimeToUTC(toWallClock(toStart), cityCode);

    const payload = {
      utcScheduledDate: utcStart.toISOString(),
      utcScheduledTime: utcStart.toISOString().substring(11, 19),
      notifyTechnicians,
    };
    if (includeDuration && toEnd) {
      payload.estimatedDuration = Math.round((toEnd - toStart) / 60000);
    }

    const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    setRescheduleConfirm({ ...pending, saving: true });
    try {
      await axios.put(`${apiBase}/api/jobs/${jobId}`, payload, { headers });
    } catch (error) {
      console.error('Error rescheduling job:', error);
      pending.info.revert();
      setRescheduleConfirm(null);
      setDragFeedback(`Could not reschedule: ${error.response?.data?.error || error.message}`);
      setTimeout(() => setDragFeedback(null), 5000);
      return;
    }

    const notes = [];
    if (notifyTechnicians) notes.push('technicians emailed');
    if (notifyCustomer) {
      try {
        await axios.post(`${apiBase}/api/jobs/${jobId}/customer-notifications`, {}, { headers });
        notes.push('customer emailed');
      } catch (error) {
        notes.push(`customer NOT emailed: ${error.response?.data?.error || error.message}`);
      }
    }
    setRescheduleConfirm(null);
    setDragFeedback(`Job rescheduled${notes.length ? ` (${notes.join(', ')})` : ' (no one emailed)'}`);
    setTimeout(() => setDragFeedback(null), 5000);
    refreshJob(jobId);
  };

  const handleEventDrop = (info) => openRescheduleConfirm(info, { includeDuration: false });

  const handleEventResize = (info) => {
    if (info.event.end && info.event.end - info.event.start < 60 * 60000) {
      info.revert();
      setDragFeedback('Minimum job duration is 1 hour');
      setTimeout(() => setDragFeedback(null), 3000);
      return;
    }
    openRescheduleConfirm(info, { includeDuration: true });
  };

  // Handler for assigning selected maids
  const handleAssignSelected = async () => {
    if (!selectedEvent?.job) {
      console.error('handleAssignSelected: no job in selectedEvent', selectedEvent);
      alert('Unable to assign: job data is missing. Please close and reopen the event.');
      return;
    }
    if (selectedMaids.length === 0) {
      alert('Please select at least one technician before assigning.');
      return;
    }
    setAssigning(true);
    try {
      // Call job assignment API to assign multiple maids to the job
      // Strip "job_" prefix if present to get the actual UUID
      const jobId = selectedEvent.job.id.startsWith('job_') 
        ? selectedEvent.job.id.substring(4)  
        : selectedEvent.job.id;
      const response = await axios.post(
        `${apiBase}/api/jobs/${jobId}/assign-maids`,
        {
          maidIds: selectedMaids,
          payouts: Object.fromEntries(selectedMaids.map((id) => [id, Number(teamPayouts[id])])),
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      console.log('Successfully assigned maids to job:', response.data);
      
      // Show success message
      alert(`Successfully assigned ${selectedMaids.length} technician${selectedMaids.length > 1 ? 's' : ''} to job!`);
      
      setEventModalOpen(false);
      setSelectedMaids([]);
      setEditingAssignments(false);
      setTeamPickerOpen(false);
      
      // Refresh job data to show updated assignments
      window.location.reload(); // Simple refresh for now - TODO: Implement partial data refresh
      
    } catch (err) {
      console.error('Error assigning maids:', err);
      const errorMessage = err.response?.data?.error || 'Failed to assign technicians.';
      alert(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  // Handler for removing a specific maid from assignment
  const handleRemoveMaid = async (maidId, maidName) => {
    setConfirmRemoval({ open: true, maidId, maidName });
  };

  // Handler for confirming maid removal
  const handleConfirmRemoveMaid = async () => {
    const { maidId } = confirmRemoval;
    setConfirmRemoval({ open: false, maidId: null, maidName: '' });
    
    if (!selectedEvent?.job || !maidId) return;
    
    setAssigning(true);
    try {
      // Get current assignments and remove the specified maid
      const currentAssignments = selectedEvent.job.assignments || [];
      const remainingMaidIds = currentAssignments
        .filter(assignment => assignment.maid?.id !== maidId && assignment.maid?.userId !== maidId)
        .map(assignment => assignment.maid?.id || assignment.maid?.userId)
        .filter(Boolean);

      const jobId = selectedEvent.job.id.startsWith('job_') 
        ? selectedEvent.job.id.substring(4)  
        : selectedEvent.job.id;
        
      const response = await axios.post(
        `${apiBase}/api/jobs/${jobId}/assign-maids`,
        { maidIds: remainingMaidIds },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      console.log('Successfully removed maid from job:', response.data);
      alert('Technician removed from assignment successfully!');
      
      setEventModalOpen(false);
      setSelectedMaids([]);
      setEditingAssignments(false);
      setTeamPickerOpen(false);
      window.location.reload();
      
    } catch (err) {
      console.error('Error removing maid:', err);
      const errorMessage = err.response?.data?.error || 'Failed to remove technician.';
      alert(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  // Handler for removing all assignments
  const handleRemoveAllAssignments = async () => {
    setConfirmRemoveAll(false);
    
    if (!selectedEvent?.job) return;
    
    setAssigning(true);
    try {
      const jobId = selectedEvent.job.id.startsWith('job_') 
        ? selectedEvent.job.id.substring(4)  
        : selectedEvent.job.id;
        
      const response = await axios.post(
        `${apiBase}/api/jobs/${jobId}/assign-maids`,
        { maidIds: [] }, // Empty array removes all assignments
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      console.log('Successfully removed all assignments:', response.data);
      alert('All assignments removed successfully!');
      
      setEventModalOpen(false);
      setSelectedMaids([]);
      setEditingAssignments(false);
      setTeamPickerOpen(false);
      window.location.reload();
      
    } catch (err) {
      console.error('Error removing all assignments:', err);
      const errorMessage = err.response?.data?.error || 'Failed to remove assignments.';
      alert(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  // Handler for deleting a job
  // Delete the job chosen in the confirmation dialog — from the job dialog,
  // a table row or a board card (the dialog doesn't need to be open)
  const handleDeleteJob = async () => {
    const { jobId: rawJobId } = confirmDeleteJob;
    setConfirmDeleteJob({ open: false, jobId: null, jobTitle: '' });
    if (!rawJobId) return;

    const jobId = String(rawJobId).replace(/^job_/, '');
    const isThisJob = (job) => job && String(job.id).replace(/^job_/, '') === jobId;

    setAssigning(true);
    try {
      await axios.delete(
        `${apiBase}/api/jobs/${jobId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      // Drop it from every view without reloading the page
      setEvents((prev) => prev.filter((ev) => !isThisJob(ev.extendedProps?.job)));
      setVisibleEvents((prev) => prev.filter((ev) => !isThisJob(ev.extendedProps?.job)));
      if (isThisJob(selectedEvent?.job)) {
        setEventModalOpen(false);
        setSelectedEvent(null);
        setSelectedMaids([]);
        setEditingAssignments(false);
        setTeamPickerOpen(false);
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      alert(err.response?.data?.error || 'Failed to delete job.');
    } finally {
      setAssigning(false);
    }
  };

  // Handler to enter edit mode for the job's own fields (admin only)
  const handleStartEditJob = () => {
    const job = selectedEvent?.job;
    if (!job) return;
    const address = job.address || {};

    // Show the schedule in city timezone — the same one the calendar displays.
    // utcScheduledDate is the full UTC timestamp; slicing it raw showed UTC
    // values that disagreed with the calendar (and rolled the date for
    // evening jobs).
    let scheduledDate = '';
    let scheduledTime = '';
    if (job.utcScheduledDate) {
      const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
      const formatted = new Intl.DateTimeFormat('en-CA', {
        timeZone: getTimezoneForCity(cityCode),
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).format(new Date(job.utcScheduledDate));
      [scheduledDate, scheduledTime] = formatted.split(', ');
    } else if (job.utcScheduledTime) {
      scheduledTime = job.utcScheduledTime.substring(0, 5);
    }

    const lines = fromApiLineItems(job.lineItems);
    setJobEditExtras(lines.extras);
    setJobEditForm({
      status: job.status || 'NOT_STARTED',
      jobType: job.jobType || 'RESIDENTIAL',
      serviceType: job.serviceType || '',
      scheduledDate,
      scheduledTime,
      estimatedDuration: job.estimatedDuration || '',
      totalAmount: lines.mainPrice != null ? lines.mainPrice : job.totalAmount != null ? (job.totalAmount / 100) : '',
      mainTechnicianId: lines.mainTechnicianId,
      specialInstructions: job.specialInstructions || '',
      notes: job.notes || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
    });
    setJobEditError(null);
    setEditingJobDetails(true);
  };

  const handleCancelEditJob = () => {
    setEditingJobDetails(false);
    setJobEditForm(null);
    setJobEditExtras([]);
    setJobEditError(null);
  };

  // Handler for saving edited job fields (admin only - PUT /api/jobs/:id)
  const handleSaveJobDetails = async () => {
    if (!selectedEvent?.job || !jobEditForm) return;

    const jobId = selectedEvent.job.id.startsWith('job_')
      ? selectedEvent.job.id.substring(4)
      : selectedEvent.job.id;

    const hadLines = (selectedEvent.job.lineItems || []).length > 0;
    if (jobEditExtras.length > 0 && (jobEditForm.totalAmount === '' || !extrasValid(jobEditExtras))) {
      setJobEditError('Enter the main service price and a service and price for each add-on.');
      return;
    }

    setSavingJobDetails(true);
    setJobEditError(null);
    try {
      // Form fields are in city timezone (matching the calendar); convert the
      // combined date+time back to UTC. Sending a bare date here used to
      // truncate utcScheduledDate to midnight UTC on every save.
      let utcScheduledDate = null;
      let utcScheduledTime = null;
      if (jobEditForm.scheduledDate && jobEditForm.scheduledTime) {
        const cityCode = process.env.REACT_APP_CITY_CODE || 'ORL';
        const utc = convertCityTimeToUTC(
          `${jobEditForm.scheduledDate}T${jobEditForm.scheduledTime}:00`,
          cityCode
        );
        utcScheduledDate = utc.toISOString();
        utcScheduledTime = utc.toISOString().substring(11, 19);
      }

      const payload = {
        status: jobEditForm.status,
        jobType: jobEditForm.jobType,
        serviceType: jobEditForm.serviceType || null,
        utcScheduledDate,
        utcScheduledTime,
        estimatedDuration: jobEditForm.estimatedDuration ? parseInt(jobEditForm.estimatedDuration, 10) : null,
        totalAmount: jobEditForm.totalAmount !== ''
          ? Math.round((parseFloat(jobEditForm.totalAmount) + extrasTotal(jobEditExtras)) * 100)
          : null,
        specialInstructions: jobEditForm.specialInstructions || null,
        notes: jobEditForm.notes || null,
        address: {
          street: jobEditForm.street || null,
          city: jobEditForm.city || null,
          state: jobEditForm.state || null,
          zip: jobEditForm.zip || null,
          country: 'US',
          street_line_2: null,
        },
      };

      await axios.put(
        `${apiBase}/api/jobs/${jobId}`,
        payload,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      // Add-on lines (the server sets the job total to their sum)
      if (!selectedEvent.job.bookingId && (hadLines || jobEditExtras.length > 0 || jobEditForm.mainTechnicianId)) {
        await axios.put(
          `${apiBase}/api/jobs/${jobId}/line-items`,
          { lineItems: toApiLineItems(jobEditForm.totalAmount, jobEditExtras, { mainTechnicianId: jobEditForm.mainTechnicianId }) },
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
      }

      // Re-fetch the saved job so the calendar, board, table and this dialog
      // all show it in the same shape (merging the raw API job here used to
      // leave the schedule views showing the old date/time/price)
      await refreshJob(jobId);
      setEditingJobDetails(false);
      setJobEditForm(null);
      setJobEditExtras([]);
    } catch (err) {
      console.error('Error updating job:', err);
      setJobEditError(err.response?.data?.error || 'Failed to update job.');
    } finally {
      setSavingJobDetails(false);
    }
  };

  // Handler for adding a task to a job (admin only - POST /api/jobs/:id/tasks)
  const handleAddTask = async () => {
    if (!selectedEvent?.job || !newTaskForm.title.trim()) return;

    const jobId = selectedEvent.job.id.startsWith('job_')
      ? selectedEvent.job.id.substring(4)
      : selectedEvent.job.id;

    setTaskActionLoading(true);
    setTaskActionError(null);
    try {
      const response = await axios.post(
        `${apiBase}/api/jobs/${jobId}/tasks`,
        { title: newTaskForm.title.trim(), category: newTaskForm.category || 'General', isRequired: false },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setSelectedEvent((prev) => ({
        ...prev,
        job: { ...prev.job, tasks: [...(prev.job.tasks || []), response.data] },
      }));
      setNewTaskForm({ title: '', category: '' });
    } catch (err) {
      console.error('Error adding task:', err);
      setTaskActionError(err.response?.data?.error || 'Failed to add task.');
    } finally {
      setTaskActionLoading(false);
    }
  };

  // Handler for removing a task from a job (admin only - DELETE /api/jobs/:jobId/tasks/:taskId)
  const handleDeleteTask = async (taskId) => {
    if (!selectedEvent?.job) return;

    const jobId = selectedEvent.job.id.startsWith('job_')
      ? selectedEvent.job.id.substring(4)
      : selectedEvent.job.id;

    setTaskActionLoading(true);
    setTaskActionError(null);
    try {
      await axios.delete(
        `${apiBase}/api/jobs/${jobId}/tasks/${taskId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setSelectedEvent((prev) => ({
        ...prev,
        job: { ...prev.job, tasks: (prev.job.tasks || []).filter((t) => t.id !== taskId) },
      }));
    } catch (err) {
      console.error('Error removing task:', err);
      setTaskActionError(err.response?.data?.error || 'Failed to remove task.');
    } finally {
      setTaskActionLoading(false);
    }
  };

  // Handler for syncing job to HousecallPro
  const handleSyncToHCP = async () => {
    if (!selectedEvent?.job) return;

    const job = selectedEvent.job;

    // Validate job can be synced
    if (job.sourceSystem !== 'SMOOBU') {
      alert('Only Smoobu jobs can be synced to HousecallPro');
      return;
    }

    if (job.hcpJobId) {
      alert(`This job is already synced to HCP (HCP Job ID: ${job.hcpJobId})`);
      return;
    }

    if (!job.clientProfileId || !job.clientProfile) {
      alert('Assign a Property Manager to this job before syncing to HCP.');
      return;
    }

    setSyncingToHCP(true);
    setSyncResult(null);

    try {
      const jobId = job.id.startsWith('job_') ? job.id.substring(4) : job.id;

      console.log('[HCP Sync] Syncing job to HCP:', jobId);

      const response = await axios.post(
        `${apiBase}/api/admin/hcp/sync-job-to-hcp`,
        { jobId },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      console.log('[HCP Sync] Successfully synced job:', response.data);

      setSyncResult({
        success: true,
        message: `Job synced successfully! HCP Job ID: ${response.data.data.hcpJobId}`,
        data: response.data.data
      });

      // Update the selected event with HCP details
      setSelectedEvent(prev => ({
        ...prev,
        job: {
          ...prev.job,
          hcpJobId: response.data.data.hcpJobId,
          hcpJobNumber: response.data.data.hcpJobNumber
        }
      }));

      // Refresh jobs after a short delay to show the updated HCP sync status
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (err) {
      console.error('[HCP Sync] Error syncing job:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.error || 'Failed to sync job to HCP';

      setSyncResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setSyncingToHCP(false);
    }
  };

  // Handler for linking/unlinking job to property manager client
  const handleLinkJobToClient = async (clientProfileId) => {
    if (!selectedEvent?.job) return;

    setLinkingClient(true);

    try {
      const jobId = selectedEvent.job.id.startsWith('job_')
        ? selectedEvent.job.id.substring(4)
        : selectedEvent.job.id;

      const response = await axios.patch(
        `${apiBase}/api/jobs/${jobId}/client`,
        { clientProfileId: clientProfileId || null },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        // Update selected event with new client profile link
        setSelectedEvent(prev => ({
          ...prev,
          job: {
            ...prev.job,
            clientProfileId: clientProfileId,
            clientProfile: response.data.data.job.clientProfile
          }
        }));

        // Show success message
        const message = clientProfileId
          ? 'Job linked to property manager successfully!'
          : 'Job unlinked from property manager';
        alert(message);

        // Optionally refresh events to show updated data
        // You can remove this if you want to avoid page reload
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error linking job to client:', error);
      const errorMessage = error.response?.data?.error || 'Failed to link job to property manager';
      alert(errorMessage);
    } finally {
      setLinkingClient(false);
    }
  };

  // Handler for starting assignment editing mode
  const handleEditAssignments = () => {
    setEditingAssignments(true);
    // Pre-select currently assigned maids
    if (selectedEvent?.job?.assignments) {
      const currentMaidIds = selectedEvent.job.assignments
        .map(assignment => assignment.maid?.id || assignment.maid?.userId)
        .filter(Boolean);
      setSelectedMaids(currentMaidIds);
      // Start from each technician's current payout
      setTeamPayouts(Object.fromEntries(
        selectedEvent.job.assignments
          .filter((a) => (a.maid?.id || a.maid?.userId) && a.paymentAmount != null)
          .map((a) => [a.maid?.id || a.maid?.userId, String(Number(a.paymentAmount))])
      ));
    }
  };

  // "Notify technicians": re-sends the standard assignment email (same as on assignment) to the job's technicians
  const handleNotifyTechnicians = () => {
    if (!selectedEvent?.job) return;
    const recipients = (selectedEvent.job.assignments || [])
      .filter((a) => a.maid && a.status !== 'DECLINED')
      .map((a) => ({
        id: a.maid.id,
        name: `${a.maid.firstName || ''} ${a.maid.lastName || ''}`.trim(),
        email: a.maid.email || '',
        role: a.role,
      }));
    setNotificationResult(null);
    setNotifyDialog({ recipients, note: '' });
  };

  const handleSendNotifications = async () => {
    if (!selectedEvent?.job || !notifyDialog) return;
    setNotifyingTechnicians(true);
    try {
      const jobId = selectedEvent.job.id.startsWith('job_') ? selectedEvent.job.id.substring(4) : selectedEvent.job.id;
      const { data } = await axios.post(
        `${apiBase}/api/jobs/${jobId}/notify-technicians`,
        { note: notifyDialog.note },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      const missed = (data.recipients || []).filter((r) => !r.sent).map((r) => r.name || 'a technician');
      setNotificationResult({
        success: data.sent > 0,
        message: data.sent > 0
          ? `Email sent to ${data.sent} technician${data.sent > 1 ? 's' : ''}`
          : 'No email was sent',
        details: missed.length ? `Not sent to: ${missed.join(', ')} (no email address or sending failed)` : null,
      });
      setNotifyDialog(null);
    } catch (error) {
      setNotificationResult({
        success: false,
        message: error.response?.data?.error || 'Failed to send the emails',
        details: error.response?.data?.details,
      });
      setNotifyDialog(null);
    } finally {
      setNotifyingTechnicians(false);
    }
  };

  // Fetch availability for all maids for the week of the booking (7x8 grid)
  const fetchMaidAvailabilities = async (maids, bookingDate) => {
    if (!maids || maids.length === 0 || !bookingDate) return;
    const weekStart = format(startOfWeek(new Date(bookingDate), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const promises = maids.map(async maid => {
      try {
        const res = await axios.get(
          `${apiBase}/api/maids/${maid.userId || maid.id}/availability?weekStart=${weekStart}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        // Assume API returns { grid: [[bool,...], ...] } for 8 rows x 7 cols
        return { id: maid.userId || maid.id, grid: res.data.grid };
      } catch {
        // fallback: all unavailable
        return { id: maid.userId || maid.id, grid: Array(8).fill().map(() => Array(7).fill(false)) };
      }
    });
    const results = await Promise.all(promises);
    const availMap = {};
    results.forEach(r => { availMap[r.id] = r.grid; });
    setMaidAvailabilities(availMap);
  };

  // When maidMatches or selectedEvent changes, fetch availabilities
  useEffect(() => {
    if (eventModalOpen && maidMatches.length > 0 && selectedEvent?.booking?.serviceDate) {
      fetchMaidAvailabilities(maidMatches, selectedEvent.booking.serviceDate);
    }
    // eslint-disable-next-line
  }, [eventModalOpen, maidMatches, selectedEvent?.booking?.serviceDate]);

  // Fetch weekly grid when modal opens and maidMatches/selectedEvent changes
  useEffect(() => {
    const fetchWeeklyGrid = async () => {
      if (!eventModalOpen || !selectedEvent?.booking || maidMatches.length === 0) return;
      setWeeklyAvailLoading(true);
      setWeeklyAvailError(null);
      try {
        const weekStart = format(startOfWeek(new Date(selectedEvent.booking.serviceDate), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const zipcode = selectedEvent.booking?.zipCode || selectedEvent.booking?.address?.zipCode || selectedEvent.job?.address?.zipCode;
        const res = await axios.get(`${apiBase}/api/maids/weekly-availability`, {
          params: {
            serviceType: selectedEvent.booking.mainService,
            serviceArea: zipcode,
            weekStart
          },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        // Map by maidId for fast lookup
        const gridMap = {};
        (res.data.results || []).forEach(r => { gridMap[r.maid.userId] = r.grid; });
        setWeeklyAvailGrid(gridMap);
        setWeeklyAvailSlotDefs(res.data.slotDefs || []);
      } catch (err) {
        setWeeklyAvailError('Failed to load weekly availability');
      } finally {
        setWeeklyAvailLoading(false);
      }
    };
    fetchWeeklyGrid();
  }, [eventModalOpen, selectedEvent, maidMatches, apiBase]);

  // --- Legend ---
  const Legend = () => (
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 16, height: 16, bgcolor: '#F44336', borderRadius: '50%' }} /> <Typography variant="caption">No technician assigned</Typography></Box>
      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 16, height: 16, bgcolor: '#4CAF50', borderRadius: '50%' }} /> <Typography variant="caption">In Progress</Typography></Box>
      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 16, height: 16, bgcolor: '#2196F3', borderRadius: '50%' }} /> <Typography variant="caption">Completed</Typography></Box>
      <Box display="flex" alignItems="center" gap={1}><Box sx={{ width: 16, height: 16, bgcolor: '#FFA500', borderRadius: '50%' }} /> <Typography variant="caption">Not Started</Typography></Box>
    </Box>
  );

  // ---- Job Details window ----
  const closeJobDetails = () => {
    if (editingJobDetails) handleCancelEditJob();
    setTeamPickerOpen(false);
    setJobMenuAnchor(null);
    setEventModalOpen(false);
  };
  const openTeamPicker = () => {
    if (selectedEvent?.job?.assignments?.length) handleEditAssignments();
    setTeamPickerOpen(true);
  };
  const closeTeamPicker = () => {
    setTeamPickerOpen(false);
    setEditingAssignments(false);
    setSelectedMaids([]);
    setMaidSearchQuery('');
  };
  const setJobEditMainPrice = (value) => setJobEditForm((f) => ({ ...f, totalAmount: value }));
  const setJobEditMainTechnician = useCallback((technicianId) => setJobEditForm((f) => (f ? { ...f, mainTechnicianId: technicianId } : f)), []);

  const detailJob = selectedEvent?.job || null;
  const detailContact = (() => {
    if (!selectedEvent) return null;
    const job = selectedEvent.job || {};
    const hcpCustomer = job.hcpRawData?.customer;
    const address = [job.address, selectedEvent.booking?.address, hcpCustomer?.address, job.hcpRawData?.address]
      .filter(Boolean)
      .map((a) => formatJobAddress(a))
      .find((text) => text && text.trim() && text !== 'Address not available') || null;
    return {
      name: job.customer?.customerName || job.customer?.name || selectedEvent.title || 'Unknown customer',
      phone: hcpCustomer?.mobile_number || hcpCustomer?.work_number || job.customer?.customerPhone || job.customer?.phone || selectedEvent.booking?.client?.phoneNumber || null,
      email: job.customer?.customerEmail || job.customer?.email || selectedEvent.booking?.customerEmail || selectedEvent.booking?.client?.email || hcpCustomer?.email || null,
      address,
    };
  })();
  // formatCityTime always adds date and time; the header needs them apart
  const detailTimeZone = getTimezoneForCity(process.env.REACT_APP_CITY_CODE || 'ORL');
  const formatDetail = (date, options) => new Intl.DateTimeFormat('en-US', { timeZone: detailTimeZone, ...options }).format(new Date(date));
  const detailStart = detailJob?.utcScheduledDate || selectedEvent?.start || null;
  const detailWhen = detailStart
    ? formatDetail(detailStart, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : 'Date not set';
  const detailTimeRange = detailStart
    ? formatDetail(detailStart, { hour: 'numeric', minute: '2-digit' })
      + (detailJob?.estimatedDuration
        ? `–${formatDetail(new Date(detailStart).getTime() + detailJob.estimatedDuration * 60000, { hour: 'numeric', minute: '2-digit' })}`
        : '')
    : null;
  const detailServiceLabel = SERVICE_TYPES.find((t) => t.value === detailJob?.serviceType)?.label || detailJob?.serviceType || null;
  const detailLines = [...(detailJob?.lineItems || [])].sort((a, b) => a.position - b.position);
  const detailTechName = (userId) => {
    const a = (detailJob?.assignments || []).find((x) => (x.maidId || x.maid?.id) === userId);
    return a?.maid ? `${a.maid.firstName || ''} ${a.maid.lastName || ''}`.trim() : null;
  };
  const detailActiveAssignments = (detailJob?.assignments || []).filter((a) => a.status !== 'DECLINED');
  const detailPayoutsShown = detailJob ? payoutShownFor(detailJob.utcScheduledDate) : false;
  const detailPayoutsKnown = detailPayoutsShown && detailActiveAssignments.length > 0
    && detailActiveAssignments.every((a) => a.paymentAmount != null);
  const detailPayoutTotal = detailActiveAssignments.reduce((sum, a) => sum + (a.paymentAmount != null ? Number(a.paymentAmount) : 0), 0);
  const detailTotal = detailJob?.totalAmount != null ? detailJob.totalAmount / 100 : null;
  const editTechnicians = useMemo(() => detailActiveAssignments
    .filter((a) => a.maid)
    .map((a) => ({ userId: a.maidId || a.maid.id, name: `${a.maid.firstName || ''} ${a.maid.lastName || ''}`.trim() })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [detailJob?.assignments]);

  return (
    <AdminPageLayout>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">Service Schedule</Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{ ml: 2 }}
          >
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarViewMonthIcon />
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="board" aria-label="board view">
              <ViewKanbanIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'table' && (
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={handleExportCSV}
              size="small"
              sx={{ ml: 2 }}
            >
              Export CSV
            </Button>
          )}
          
          {/* Maid Name Filter */}
          <TextField
            placeholder="Filter by technician name..."
            value={maidNameFilter}
            onChange={(e) => setMaidNameFilter(e.target.value)}
            size="small"
            sx={{ ml: 2, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: maidNameFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setMaidNameFilter('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Source System Filter */}
          <FormControl size="small" sx={{ ml: 2, minWidth: 150 }}>
            <Select
              value={sourceSystemFilter}
              onChange={(e) => setSourceSystemFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All Sources</MenuItem>
              <MenuItem value="smoobu">Smoobu</MenuItem>
              <MenuItem value="hcp">HCP</MenuItem>
              <MenuItem value="local">Local</MenuItem>
            </Select>
          </FormControl>

          {/* Upcoming Jobs Filter (table view) */}
          {viewMode === 'table' && (
            <FormControlLabel
              control={
                <Switch
                  checked={upcomingOnly}
                  onChange={(e) => setUpcomingOnly(e.target.checked)}
                  size="small"
                />
              }
              label="Upcoming only"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
            Total Job Amount
          </Typography>
          <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold' }}>
            {formatCurrency(totalJobAmount)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {(() => {
              const count = viewMode === 'table' ? sortedEvents.length :
                           viewMode === 'board' ? boardEvents.length :
                           (visibleEvents.length > 0 ? visibleEvents.length : filteredEvents.length);
              const visibilityText = viewMode === 'calendar' && visibleEvents.length > 0 ? 'visible' : 'scheduled';
              return `${count} job${count !== 1 ? 's' : ''} ${visibilityText}`;
            })()}
          </Typography>
        </Box>
      </Box>
      
      {/* Drag and Drop Feedback */}
      {dragFeedback && (
        <Alert severity={/^(Could not|Minimum)/.test(dragFeedback) ? 'error' : /NOT emailed/.test(dragFeedback) ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {dragFeedback}
        </Alert>
      )}
      
      <Legend />
      
      {/* Hint for events clipped out of the timeGrid's visible hours */}
      {viewMode === 'calendar' && clippedEvents.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {clippedEvents.length} job{clippedEvents.length > 1 ? 's' : ''} in this period{' '}
            {clippedEvents.length > 1 ? 'are' : 'is'} scheduled outside the visible hours
            ({SLOT_MIN_HOUR}:00 – {SLOT_MAX_HOUR}:00) and {clippedEvents.length > 1 ? "don't" : "doesn't"} appear
            on this grid — click to open:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {clippedEvents.map((ev) => {
              const start = new Date(ev.start);
              const label = `${ev.title || 'Job'} · ${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
              return (
                <Chip
                  key={ev.id}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={label}
                  onClick={() => handleEventClick({ event: ev })}
                />
              );
            })}
          </Stack>
        </Alert>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Box sx={{
          width: '100%',
          height: 'calc(100vh - 150px)',
          mb: 3,
          '& .fc-timegrid-slot': {
            height: '50px !important'
          },
          '& .fc-col-header-cell': {
            minWidth: '120px !important'
          },
          '& .fc-timegrid-col': {
            minWidth: '120px !important'
          },
          '& .fc-scroller': {
            overflow: 'auto !important'
          }
        }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridTwoWeek"
            timeZone="local"
            events={filteredEvents}
            views={{
              timeGridTwoWeek: {
                type: 'timeGrid',
                duration: { weeks: 2 },
                buttonText: '2 weeks'
              },
              dayGridMonth: {
                type: 'dayGrid',
                buttonText: 'Month',
                dayMaxEvents: 3,
                moreLinkClick: 'popover'
              }
            }}
            headerToolbar={{ 
              left: 'prev,next today', 
              center: 'title', 
              right: 'dayGridMonth,timeGridTwoWeek,timeGridWeek,timeGridDay' 
            }}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            datesSet={handleDatesSet}
            height="100%"
            editable={true}
            eventResizableFromStart={true}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventOverlap={false}
            slotDuration="01:00:00"
            slotLabelInterval="01:00:00"
            slotMinTime={`${String(SLOT_MIN_HOUR).padStart(2, '0')}:00:00`}
            slotMaxTime={`${String(SLOT_MAX_HOUR).padStart(2, '0')}:00:00`}
            dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
            dayMaxEvents={3}
            moreLinkText="more"
          />
        </Box>
      )}
      
      {/* Board (Kanban) View */}
      {viewMode === 'board' && (
        <Box sx={{ mb: 3 }}>
          <PeriodFilter
            periodType={boardPeriodType}
            anchor={boardAnchor}
            start={boardRangeStart}
            end={boardRangeEnd}
            onPeriodTypeChange={setBoardPeriodType}
            onAnchorChange={setBoardAnchor}
          />
          <Grid container spacing={2}>
            {BOARD_COLUMNS.map((column) => {
              const columnEvents = boardEvents.filter(
                (ev) => boardColumnForStatus(ev.extendedProps?.job?.status) === column.id
              );
              return (
                <Grid item xs={12} sm={6} md={3} key={column.id}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2, height: '100%' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {column.title}
                      </Typography>
                      <Chip
                        label={columnEvents.length}
                        size="small"
                        sx={{
                          bgcolor: jobService.getJobStatusColor(column.id),
                          color: '#fff',
                          fontWeight: 600
                        }}
                      />
                    </Box>

                    <Stack spacing={1.5} sx={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                      {columnEvents.map((ev) => {
                        const job = ev.extendedProps?.job || {};
                        const booking = ev.extendedProps?.booking || {};
                        const maids = job.assignments || [];
                        let timeLabel = 'TBD';
                        try {
                          timeLabel = format(new Date(ev.start), 'MMM d, p');
                        } catch (e) {
                          timeLabel = 'TBD';
                        }
                        return (
                          <Card
                            key={ev.id}
                            elevation={2}
                            onClick={() => handleEventClick({ event: ev })}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s ease'
                              }
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                {booking.clientName || 'Unknown Customer'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {booking.mainService || job.jobType || 'Cleaning Service'}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {timeLabel}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600} sx={{ color: 'success.main' }}>
                                    {job.totalAmount ? formatCurrency(job.totalAmount) : 'N/A'}
                                  </Typography>
                                  <PaymentStateChip state={job.paymentState} scheduledDate={job.utcScheduledDate} />
                                  {job.paymentState === 'UNPAID' && job.invoiceSentAt && (
                                    <Tooltip title={`Invoice emailed ${new Date(job.invoiceSentAt).toLocaleString('en-US')}${job.invoiceSendCount > 1 ? ` (${job.invoiceSendCount} times)` : ''}`}>
                                      <Typography variant="caption" color="text.secondary">✉ Sent</Typography>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {maids.length > 0 ? (
                                    <Chip
                                      icon={<PersonIcon sx={{ fontSize: 14 }} />}
                                      label={maids.length}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Chip label="Unassigned" size="small" color="error" variant="outlined" />
                                  )}
                                  <Tooltip title="Delete job">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label="Delete job"
                                      onClick={(e) => {
                                        e.stopPropagation(); // don't open the job dialog
                                        setConfirmDeleteJob({
                                          open: true,
                                          jobId: job.id,
                                          jobTitle: booking.clientName || job.customer?.customerName || 'this customer',
                                        });
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {columnEvents.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No jobs.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Paper sx={{ mb: 3 }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'scheduledStartTime'}
                      direction={sortBy === 'scheduledStartTime' ? sortOrder : 'asc'}
                      onClick={() => handleSort('scheduledStartTime')}
                    >
                      Date & Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'customer'}
                      direction={sortBy === 'customer' ? sortOrder : 'asc'}
                      onClick={() => handleSort('customer')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'service'}
                      direction={sortBy === 'service' ? sortOrder : 'asc'}
                      onClick={() => handleSort('service')}
                    >
                      Service
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'amount'}
                      direction={sortBy === 'amount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('amount')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Property Manager</TableCell>
                  <TableCell>Assigned Team</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                ) : sortedEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No jobs scheduled
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedEvents.map((event) => {
                    const booking = event.extendedProps?.booking;
                    const job = event.extendedProps?.job;

                    return (
                      <TableRow 
                        key={event.id}
                        sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCityTime(event.start, process.env.REACT_APP_CITY_CODE || 'ORL', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCityTime(event.start, process.env.REACT_APP_CITY_CODE || 'ORL', { 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                hour12: true 
                              })} - 
                              {event.end ? formatCityTime(event.end, process.env.REACT_APP_CITY_CODE || 'ORL', { 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                hour12: true 
                              }) : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {booking?.clientName || 'Unknown Customer'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking?.clientEmail || 'No email'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {booking?.mainService || 'Unknown Service'}
                          </Typography>
                          {job?.estimatedDuration && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Duration: {job.estimatedDuration} min
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Display rental amount for Smoobu jobs, job amount for others */}
                          {job?.sourceSystem === 'SMOOBU' ? (
                            <>
                              {(job?.rentalAmount || booking?.rentalAmount) && (
                                <Typography variant="caption" sx={{ color: 'info.main', display: 'block' }}>
                                  Rental: {formatCurrency(job?.rentalAmount || booking?.rentalAmount)}
                                </Typography>
                              )}
                              {job?.totalAmount && (
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                  Clean: {formatCurrency(job.totalAmount)}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {job?.totalAmount ? formatCurrency(job.totalAmount) : 'N/A'}
                            </Typography>
                          )}
                          {(job?.outstanding_balance || job?.outstandingBalance) > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Outstanding: {formatCurrency(job?.outstanding_balance || job?.outstandingBalance)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking?.status || job?.status || 'Unknown'}
                            size="small"
                            color={
                              (booking?.status === 'completed' || job?.status === 'completed') ? 'success' :
                              (booking?.status === 'in_progress' || job?.status === 'in_progress') ? 'info' :
                              'default'
                            }
                            sx={{
                              backgroundColor: event.backgroundColor,
                              color: 'white'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {job?.clientProfileId && job?.clientProfile ? (
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                              {job.clientProfile.User
                                ? `${job.clientProfile.User.firstName} ${job.clientProfile.User.lastName}`
                                : 'Assigned'}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Not assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {job?.assignments?.length > 0 ? (
                            <Box>
                              {job.assignments.slice(0, 2).map((assignment, idx) => (
                                <Box key={assignment.id} display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                  <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                                    {assignment.maid?.firstName?.[0]}{assignment.maid?.lastName?.[0]}
                                  </Avatar>
                                  <Typography variant="caption">
                                    {assignment.maid?.firstName} {assignment.maid?.lastName}
                                  </Typography>
                                  <Chip 
                                    label={assignment.role} 
                                    size="small" 
                                    color={assignment.role === 'LEAD' ? 'primary' : 'default'}
                                    sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                                  />
                                </Box>
                              ))}
                              {job.assignments.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{job.assignments.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Not assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              // Use the same address extraction logic as calendar view
                              const jobAddress = job?.address;
                              const bookingAddress = booking?.address;
                              const hcpAddress = job?.hcpRawData?.customer?.address;
                              
                              if (jobAddress) {
                                return formatJobAddress(jobAddress);
                              } else if (bookingAddress) {
                                return formatJobAddress(bookingAddress);
                              } else if (hcpAddress) {
                                return formatJobAddress(hcpAddress);
                              }
                              
                              return 'Address not available';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEventClick({ event })}
                              title="View/Edit Job"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              size="small"
                              title="Assign Technicians"
                              onClick={() => handleEventClick({ event })}
                            >
                              <PersonIcon />
                            </IconButton>
                            <IconButton 
                              size="small"
                              color="error"
                              title="Delete Job"
                              onClick={() => {
                                const jobTitle = booking?.clientName || job?.customer?.customerName || 'this customer';
                                const jobId = job?.id;
                                setConfirmDeleteJob({ open: true, jobId, jobTitle });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tooltip for calendar events */}
      {tooltip.show && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: 1,
            borderRadius: 1,
            fontSize: '0.8rem',
            whiteSpace: 'pre-line',
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: 300,
            wordWrap: 'break-word'
          }}
        >
          {tooltip.content}
        </Box>
      )}

      <MaidProfileModal maid={profileMaid} open={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
      {/* Job Details: full screen — header (who/when/status + actions), main column (the job), side column (team & money) */}
      <Dialog
        open={eventModalOpen}
        onClose={closeJobDetails}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'grey.50' } }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            px: { xs: 1.5, md: 3 }, py: 1.5,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}
        >
          <IconButton onClick={closeJobDetails} title="Close" edge="start">
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {detailContact?.name || 'Job'}
              {detailJob?.jobNumber && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  #{detailJob.jobNumber}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
              <Typography variant="body2" color="text.secondary">
                {detailWhen}{detailTimeRange ? ` · ${detailTimeRange}` : ''}
                {detailJob?.estimatedDuration ? ` (${formatDuration(detailJob.estimatedDuration)})` : ''}
              </Typography>
              {detailJob?.status && (
                <Chip
                  size="small"
                  label={JOB_STATUSES.find((s) => s.value === detailJob.status)?.label || detailJob.status}
                  color={detailJob.status === 'COMPLETED' ? 'success' : detailJob.status === 'CANCELLED' ? 'error' : detailJob.status === 'IN_PROGRESS' ? 'info' : 'default'}
                />
              )}
              {detailJob?.paymentState && (
                <PaymentStateChip state={detailJob.paymentState} scheduledDate={detailJob.utcScheduledDate} />
              )}
              {editingJobDetails && <Chip size="small" color="primary" variant="outlined" label="Editing" />}
            </Box>
          </Box>

          {detailJob && !editingJobDetails && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<EditIcon />} onClick={handleStartEditJob} disabled={assigning}>
                Edit
              </Button>
              {detailActiveAssignments.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={notifyingTechnicians ? <CircularProgress size={16} /> : <MailIcon />}
                  onClick={handleNotifyTechnicians}
                  disabled={notifyingTechnicians}
                >
                  Notify Techs
                </Button>
              )}
              {detailJob.id && (
                <SendInvoiceButton
                  jobId={String(detailJob.id).replace(/^job_/, '')}
                  jobNumber={detailJob.jobNumber}
                  size="medium"
                  disabled={assigning}
                  sentAt={detailJob.invoiceSentAt}
                />
              )}
              {detailJob.id && (
                <NotifyCustomerButton
                  jobId={String(detailJob.id).replace(/^job_/, '')}
                  jobNumber={detailJob.jobNumber}
                  size="medium"
                  disabled={assigning}
                />
              )}
              {detailJob.sourceSystem === 'SMOOBU' && detailJob.hcpJobId && (
                <Chip label={`Synced to HCP: ${detailJob.hcpJobNumber || detailJob.hcpJobId}`} color="success" icon={<HandshakeIcon />} />
              )}
              <IconButton onClick={(e) => setJobMenuAnchor(e.currentTarget)} title="More actions">
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={jobMenuAnchor} open={Boolean(jobMenuAnchor)} onClose={() => setJobMenuAnchor(null)}>
                {detailJob.sourceSystem === 'SMOOBU' && !detailJob.hcpJobId && (
                  <MenuItem
                    disabled={syncingToHCP || !detailJob.clientProfileId}
                    onClick={() => { setJobMenuAnchor(null); handleSyncToHCP(); }}
                  >
                    <ListItemIcon><HandshakeIcon fontSize="small" /></ListItemIcon>
                    {syncingToHCP ? 'Syncing...' : detailJob.clientProfileId ? 'Sync to HCP' : 'Sync to HCP (assign a property manager first)'}
                  </MenuItem>
                )}
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={() => {
                    setJobMenuAnchor(null);
                    setConfirmDeleteJob({ open: true, jobId: detailJob.id, jobTitle: detailContact?.name || selectedEvent?.title || 'Unknown Job' });
                  }}
                >
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  Delete job
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>

        <DialogContent sx={{ p: { xs: 1.5, md: 3 } }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            {syncResult && (
              <Alert severity={syncResult.success ? 'success' : 'error'} onClose={() => setSyncResult(null)} sx={{ mb: 2 }}>
                {syncResult.message}
              </Alert>
            )}
            {jobEditError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobEditError(null)}>
                {jobEditError}
              </Alert>
            )}
            {notificationResult && (
              <Alert severity={notificationResult.success ? 'success' : 'error'} onClose={() => setNotificationResult(null)} sx={{ mb: 2 }}>
                <strong>{notificationResult.message}</strong>
                {notificationResult.details && <Box component="span" sx={{ display: 'block' }}>{notificationResult.details}</Box>}
              </Alert>
            )}

            {modalLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                <CircularProgress size={24} /><Typography>Loading...</Typography>
              </Box>
            ) : selectedEvent && (
              <Grid container spacing={3}>
                {/* Main column: the job */}
                <Grid item xs={12} md={teamPickerOpen ? 7 : 8} sx={{ order: { xs: 2, md: 1 } }}>
                  {/* Customer & location */}
                  <JobDetailsSection title="Customer & location">
                    {editingJobDetails && jobEditForm ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{detailContact?.name}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField label="Street address" size="small" fullWidth value={jobEditForm.street}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, street: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField label="City" size="small" fullWidth value={jobEditForm.city}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, city: e.target.value })} />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <TextField label="State" size="small" fullWidth value={jobEditForm.state}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, state: e.target.value })} />
                        </Grid>
                        <Grid item xs={8} sm={4}>
                          <TextField label="Zip" size="small" fullWidth value={jobEditForm.zip}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, zip: e.target.value })} />
                        </Grid>
                      </Grid>
                    ) : (
                      <>
                        <InfoRow label="Customer" value={detailContact?.name} />
                        <InfoRow
                          label="Phone"
                          value={detailContact?.phone}
                          placeholder="Not available"
                          icon={detailContact?.phone && (
                            <IconButton size="small" color="primary" href={`tel:${detailContact.phone}`} title="Call customer">
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          )}
                        />
                        <InfoRow
                          label="Email"
                          value={detailContact?.email}
                          placeholder="Not available"
                          icon={detailContact?.email && (
                            <IconButton size="small" color="primary" href={`mailto:${detailContact.email}`} title="Email customer">
                              <MailIcon fontSize="small" />
                            </IconButton>
                          )}
                        />
                        <InfoRow
                          label="Address"
                          value={detailContact?.address}
                          placeholder="Not available"
                          icon={detailContact?.address && (
                            <IconButton
                              size="small"
                              color="primary"
                              title="Open in Google Maps"
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailContact.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MapIcon fontSize="small" />
                            </IconButton>
                          )}
                        />
                      </>
                    )}
                  </JobDetailsSection>

                  {/* Schedule & type */}
                  <JobDetailsSection title="Schedule">
                    {editingJobDetails && jobEditForm ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField label="Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                            value={jobEditForm.scheduledDate}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, scheduledDate: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField label="Start time (Eastern)" type="time" size="small" fullWidth InputLabelProps={{ shrink: true }}
                            value={jobEditForm.scheduledTime}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, scheduledTime: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField select label="Duration" size="small" fullWidth
                            value={Number(jobEditForm.estimatedDuration) || ''}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, estimatedDuration: e.target.value })}>
                            {durationChoices(jobEditForm.estimatedDuration).map((minutes) => (
                              <MenuItem key={minutes} value={minutes}>{formatDuration(minutes)}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField select label="Status" size="small" fullWidth value={jobEditForm.status}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, status: e.target.value })}>
                            {JOB_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField select label="Job type" size="small" fullWidth value={jobEditForm.jobType}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, jobType: e.target.value })}>
                            {JOB_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField select label="Service" size="small" fullWidth value={jobEditForm.serviceType}
                            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, serviceType: e.target.value })}>
                            <MenuItem value=""><em>Not set</em></MenuItem>
                            {SERVICE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            {jobEditForm.serviceType && !SERVICE_TYPES.some((t) => t.value === jobEditForm.serviceType) && (
                              <MenuItem value={jobEditForm.serviceType}>{jobEditForm.serviceType}</MenuItem>
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container columnSpacing={3}>
                        <Grid item xs={12} sm={6}>
                          <InfoRow label="Date" value={detailWhen} />
                          <InfoRow label="Time" value={detailTimeRange} placeholder="Not set" />
                          <InfoRow label="Duration" value={detailJob?.estimatedDuration ? formatDuration(detailJob.estimatedDuration) : null} placeholder="Not set" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InfoRow label="Job type" value={JOB_TYPES.find((t) => t.value === detailJob?.jobType)?.label || detailJob?.jobType} placeholder="Not set" />
                          <InfoRow label="Service" value={detailServiceLabel} placeholder="Not set" />
                          <InfoRow label="Status" value={JOB_STATUSES.find((s) => s.value === detailJob?.status)?.label || detailJob?.status} />
                        </Grid>
                      </Grid>
                    )}
                  </JobDetailsSection>

                  {/* Services & price: the main service is the first line, like the add-ons */}
                  <JobDetailsSection title="Services & price">
                    {editingJobDetails && jobEditForm ? (
                      detailJob?.bookingId ? (
                        <TextField
                          label="Price ($)"
                          type="number"
                          size="small"
                          sx={{ maxWidth: 240 }}
                          inputProps={{ min: 0, step: 5 }}
                          value={jobEditForm.totalAmount}
                          onChange={(e) => setJobEditForm({ ...jobEditForm, totalAmount: e.target.value })}
                          disabled={detailJob?.paymentState === 'PAID'}
                          helperText={detailJob?.paymentState === 'PAID' ? 'Paid — price locked' : 'Online booking: its services are set by the booking'}
                        />
                      ) : (
                        <JobLineItemsEditor
                          extras={jobEditExtras}
                          onChange={setJobEditExtras}
                          mainPrice={jobEditForm.totalAmount}
                          disabled={detailJob?.paymentState === 'PAID'}
                          embedded
                          technicians={editTechnicians}
                          main={{
                            label: SERVICE_TYPES.find((t) => t.value === jobEditForm.serviceType)?.label || 'Cleaning',
                            price: jobEditForm.totalAmount,
                            onPriceChange: setJobEditMainPrice,
                            technicianId: jobEditForm.mainTechnicianId,
                            onTechnicianChange: setJobEditMainTechnician,
                            priceDisabled: detailJob?.paymentState === 'PAID',
                            priceHelperText: detailJob?.paymentState === 'PAID' ? 'Paid — price locked' : (jobEditForm.totalAmount === '' ? 'Required to send the invoice' : ' '),
                          }}
                        />
                      )
                    ) : (
                      <>
                        <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell>Done by</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detailLines.length > 0 ? detailLines.map((line) => (
                              <TableRow key={line.id}>
                                <TableCell>
                                  {line.kind === 'MAIN'
                                    ? <>{line.description || detailServiceLabel || 'Cleaning'} <Typography component="span" variant="caption" color="text.secondary">main service</Typography></>
                                    : `${line.description}${Number(line.quantity) !== 1 ? ` × ${Number(line.quantity)}` : ''}`}
                                </TableCell>
                                <TableCell sx={{ color: line.technicianId ? 'text.primary' : 'text.secondary' }}>
                                  {line.technicianId ? (detailTechName(line.technicianId) || 'Technician') : 'Whole team'}
                                </TableCell>
                                <TableCell align="right">{formatCurrency(line.amount) === 'N/A' ? '$0.00' : formatCurrency(line.amount)}</TableCell>
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell>{detailServiceLabel || 'Cleaning'} <Typography component="span" variant="caption" color="text.secondary">main service</Typography></TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>Whole team</TableCell>
                                <TableCell align="right">{formatCurrency(detailJob?.totalAmount)}</TableCell>
                              </TableRow>
                            )}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 700, borderBottom: 'none' }}>
                                {detailJob?.sourceSystem === 'SMOOBU' ? 'Cleaning fee' : 'Total'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 'none' }}>{formatCurrency(detailJob?.totalAmount)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        {detailJob?.bookingId && (
                          <Typography variant="caption" color="text.secondary">Online booking: the invoice lists the booking's services.</Typography>
                        )}
                      </>
                    )}
                  </JobDetailsSection>

                  {/* Instructions & notes */}
                  <JobDetailsSection title="Instructions & notes">
                    {editingJobDetails && jobEditForm ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField label="Special instructions" helperText="Shown to the technicians" size="small" fullWidth multiline minRows={4}
                            value={jobEditForm.specialInstructions}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, specialInstructions: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField label="Office notes" helperText="Not shown to the technicians" size="small" fullWidth multiline minRows={4}
                            value={jobEditForm.notes}
                            onChange={(e) => setJobEditForm({ ...jobEditForm, notes: e.target.value })} />
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Special instructions (technicians see this)</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color={detailJob?.specialInstructions || detailJob?.description || selectedEvent.booking?.specialInstructions ? 'text.primary' : 'text.disabled'}>
                            {detailJob?.specialInstructions || detailJob?.description || selectedEvent.booking?.specialInstructions || 'None'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Office notes</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color={detailJob?.notes ? 'text.primary' : 'text.disabled'}>
                            {detailJob?.notes || 'None'}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </JobDetailsSection>

                  {/* Tasks (collapsed) */}
                  <Accordion disableGutters variant="outlined" sx={{ mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Tasks ({detailJob?.tasks?.length || 0})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {taskActionError && (
                        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setTaskActionError(null)}>{taskActionError}</Alert>
                      )}
                      {detailJob?.tasks?.length > 0 ? (
                        <Box mb={1}>
                          {detailJob.tasks.map((task) => (
                            <Box key={task.id} display="flex" alignItems="center" gap={1} py={0.25}>
                              <Chip label={task.category || 'General'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              <Typography variant="body2" sx={{ flex: 1 }}>{task.title}</Typography>
                              <IconButton size="small" color="error" disabled={taskActionLoading} onClick={() => handleDeleteTask(task.id)} title="Delete task">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {tasksLoading ? 'Loading tasks…' : 'No tasks added yet'}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField size="small" placeholder="Category" value={newTaskForm.category}
                          onChange={(e) => setNewTaskForm({ ...newTaskForm, category: e.target.value })} sx={{ width: 140 }} />
                        <TextField size="small" placeholder="New task title" value={newTaskForm.title} fullWidth
                          onChange={(e) => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }} />
                        <IconButton color="primary" disabled={taskActionLoading || !newTaskForm.title.trim()} onClick={handleAddTask} title="Add task">
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Housecall Pro data (legacy imports only) */}
                  {selectedEvent.hcp && (
                    <Accordion disableGutters variant="outlined" sx={{ mb: 2, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Housecall Pro</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <InfoRow label="Invoice" value={selectedEvent.hcp.invoiceNumber} />
                        <InfoRow label="HCP status" value={selectedEvent.hcp.workStatus} />
                        <InfoRow label="Company" value={selectedEvent.hcp.hcpCompanyName} />
                        <InfoRow label="Arrival window" value={selectedEvent.hcp.arrivalWindow ? `${selectedEvent.hcp.arrivalWindow} minutes` : null} />
                        <InfoRow label="Imported" value={selectedEvent.hcp.importedAt ? new Date(selectedEvent.hcp.importedAt).toLocaleDateString() : null} />
                        {selectedEvent.hcp.hcpAssignedEmployees?.length > 0 && (
                          <InfoRow
                            label="HCP employees"
                            value={selectedEvent.hcp.hcpAssignedEmployees.map((emp) => `${emp.firstName} ${emp.lastName} (${emp.role})`).join(', ')}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Grid>

                {/* Side column: team and money, or the technician picker */}
                <Grid item xs={12} md={teamPickerOpen ? 5 : 4} sx={{ order: { xs: 1, md: 2 } }}>
                  {teamPickerOpen ? (
                    <JobDetailsSection
                      title={editingAssignments ? 'Change the team' : 'Assign technicians'}
                      action={(
                        <Button size="small" startIcon={<CloseIcon />} onClick={closeTeamPicker}>Cancel</Button>
                      )}
                      sx={{ position: { md: 'sticky' }, top: { md: 0 } }}
                    >
                  {maidMatchLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CircularProgress size={24} />
                      <Typography>Finding best matches...</Typography>
                    </Box>
                  ) : maidMatches.length === 0 ? (
                    <Typography color="text.secondary">No available technicians found.</Typography>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search technicians by name..."
                        value={maidSearchQuery}
                        onChange={(e) => { setMaidSearchQuery(e.target.value); setMaidListLimit(MAID_LIST_PAGE_SIZE); }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {filteredMaidMatches.length} of {maidMatches.length} technician{maidMatches.length > 1 ? 's' : ''} 
                        {maidSearchQuery.trim() && ` matching "${maidSearchQuery.trim()}"`}
                      </Typography>
                      
                      <Box sx={{ maxHeight: { md: 'calc(100vh - 360px)' }, overflow: 'auto' }}>
                        {filteredMaidMatches.length === 0 && maidSearchQuery.trim() ? (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No technicians found matching "{maidSearchQuery.trim()}"
                          </Typography>
                        ) : (
                          shownMaidMatches.map(maid => {
                            // Calculate booking conflict for this maid
                            // Extract target date and time using correct job field names
                            const job = selectedEvent?.job;
                            
                            // Try multiple sources for target date (job fields have utc prefix)
                            const targetDate = job?.utcScheduledDate ? 
                              getDateString(job.utcScheduledDate) : 
                              job?.utcScheduledTime ?
                                getDateString(job.utcScheduledTime) :
                                selectedEvent?.start ? getDateString(selectedEvent.start) : null;
                            
                            // Try multiple sources for target time  
                            const targetTime = job?.utcScheduledDate || 
                              job?.utcScheduledTime ||
                              selectedEvent?.start || 
                              null;
                            
                            const targetTimeSlot = targetTime ? getTimeSlot(targetTime) : null;
                            
                            // Calculate true availability - can this maid be assigned to this time slot?
                            const isAvailable = (targetDate && targetTimeSlot) ? 
                              isMaidAvailableForSlot(maid, targetDate, targetTimeSlot, allJobs) : null;
                            
                            const conflictInfo = (targetDate && targetTimeSlot) ? 
                              checkMaidBookingConflict(maid, targetDate, targetTimeSlot, allJobs) : 
                              { hasConflict: false, conflictingJobs: [] };

                            const maidName = maid.firstName && maid.lastName ? 
                              `${maid.firstName} ${maid.lastName}` : 
                              maid.user?.firstName && maid.user?.lastName ? 
                                `${maid.user.firstName} ${maid.user.lastName}` :
                                maid.name || 'Unknown Technician';
                            
                            return (
                          <Paper 
                            key={maid.id} 
                            sx={{ 
                              p: 2, 
                              mb: 1, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              border: selectedMaids.includes(maid.userId || maid.id || maid.user?.id) ? '2px solid' : '1px solid',
                              borderColor: selectedMaids.includes(maid.userId || maid.id || maid.user?.id) ? 'primary.main' : 'divider',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleMaidSelect(maid.userId || maid.id || maid.user?.id)}
                          >
                            <Checkbox
                              checked={selectedMaids.includes(maid.userId || maid.id || maid.user?.id)}
                              color="primary"
                              sx={{ p: 0.5 }}
                            />
                            <Avatar sx={{ bgcolor: 'primary.main', color: '#fff' }}>
                              {maid.firstName?.[0] || maid.user?.firstName?.[0] || maid.name?.[0] || 'M'}
                            </Avatar>
                            <Box flex={1}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {maidName}
                                </Typography>
                                {conflictInfo.hasConflict && (
                                  <Tooltip title={getConflictMessage(conflictInfo, maidName, targetTimeSlot)}>
                                    <Chip 
                                      icon={<WarningIcon />}
                                      label="Time Conflict" 
                                      size="small" 
                                      color="warning"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                                {targetTimeSlot && (
                                  <Chip 
                                    icon={<ScheduleIcon />}
                                    label={targetTimeSlot === 'AM' ? 'Morning' : 'Afternoon'} 
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  ⭐ {maid.rating || maid.user?.rating || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  🎯 {maid.experience || 0}yr
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {maid.bringsSupplies || maid.hasSupplies ? '🧹' : '🚫'} {maid.bringsSupplies || maid.hasSupplies ? 'Supplies' : 'No supplies'}
                                </Typography>
                                {(maid.minDistance || maid.distance) && (
                                  <Typography variant="caption" color="text.secondary">
                                    📍 {(maid.minDistance || maid.distance).toFixed(1)}km
                                  </Typography>
                                )}
                                <Chip 
                                  label={isAvailable === null ? 'Unknown Time Slot' : (isAvailable ? 'Available' : 'Booked')} 
                                  size="small" 
                                  color={isAvailable === null ? 'default' : (isAvailable ? 'success' : 'error')}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                              {conflictInfo.hasConflict && (
                                <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                  {getConflictMessage(conflictInfo, maidName, targetTimeSlot)}
                                </Alert>
                              )}
                              {selectedMaids.includes(maid.userId || maid.id || maid.user?.id) && (() => {
                                const selectionId = maid.userId || maid.id || maid.user?.id;
                                const value = teamPayouts[selectionId] ?? '';
                                return (
                                  <TextField
                                    size="small"
                                    type="number"
                                    required
                                    label="Payout"
                                    value={value}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setTeamPayouts((prev) => ({ ...prev, [selectionId]: e.target.value }))}
                                    error={!(Number(value) > 0)}
                                    helperText={Number(value) > 0 ? ' ' : 'Required'}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    inputProps={{ min: 0, step: 5 }}
                                    sx={{ mt: 1, width: 160 }}
                                  />
                                );
                              })()}
                            </Box>
                          </Paper>
                            );
                          })
                        )}
                        {shownMaidMatches.length < filteredMaidMatches.length && (
                          <Button
                            size="small"
                            fullWidth
                            onClick={() => setMaidListLimit((n) => n + MAID_LIST_PAGE_SIZE)}
                            sx={{ mt: 1 }}
                          >
                            Show more ({filteredMaidMatches.length - shownMaidMatches.length} more)
                          </Button>
                        )}
                      </Box>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedMaids.length === 0 || assigning || !selectedMaids.every((id) => Number(teamPayouts[id]) > 0)}
                        onClick={handleAssignSelected}
                        sx={{ mt: 2, width: '100%' }}
                        startIcon={assigning ? <CircularProgress size={16} /> : <HandshakeIcon />}
                      >
                        {assigning 
                          ? (editingAssignments ? 'Updating...' : 'Assigning...') 
                          : editingAssignments 
                            ? `Update Assignment (${selectedMaids.length} technician${selectedMaids.length > 1 ? 's' : ''})` 
                            : `Assign ${selectedMaids.length} Technician${selectedMaids.length > 1 ? 's' : ''}`
                        }
                      </Button>
                    </>
                  )}
                    </JobDetailsSection>
                  ) : (
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 0 } }}>
                      <JobDetailsSection
                        title={`Team${detailActiveAssignments.length ? ` (${detailActiveAssignments.length})` : ''}`}
                        action={detailJob && (
                          <Button size="small" variant="outlined" startIcon={detailJob.assignments?.length ? <EditIcon /> : <AddIcon />} onClick={openTeamPicker} disabled={editingJobDetails}>
                            {detailJob.assignments?.length ? 'Change' : 'Assign'}
                          </Button>
                        )}
                      >
                        {detailJob?.assignments?.length > 0 ? (
                          <>
                            {detailJob.assignments.map((assignment) => (
                              <Box key={assignment.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', opacity: assignment.status === 'DECLINED' ? 0.55 : 1 }}>
                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 600, color: '#fff', bgcolor: assignment.role === 'LEAD' ? 'primary.main' : 'grey.500' }}>
                                  {assignment.maid?.firstName?.[0]}{assignment.maid?.lastName?.[0]}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {assignment.maid?.firstName} {assignment.maid?.lastName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {assignment.role === 'LEAD' ? 'Lead' : 'Assistant'} · {(assignment.status || '').charAt(0) + (assignment.status || '').slice(1).toLowerCase()}
                                  </Typography>
                                </Box>
                                <Tooltip title={
                                  !detailPayoutsShown
                                    ? 'Payouts before Oct 6, 2026 are not shown (old automatic amounts were wrong)'
                                    : assignment.paymentAmountManual ? 'Payout set by an admin' : 'Automatic payout (half the price, split)'
                                }>
                                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 64, textAlign: 'right' }}>
                                    {detailPayoutsShown && assignment.paymentAmount != null ? `$${Number(assignment.paymentAmount).toFixed(2)}` : '—'}
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  title="Remove from the job"
                                  onClick={() => handleRemoveMaid(
                                    assignment.maid?.id || assignment.maid?.userId,
                                    `${assignment.maid?.firstName} ${assignment.maid?.lastName}`
                                  )}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Button size="small" color="error" onClick={() => setConfirmRemoveAll(true)}>Remove all</Button>
                              {detailPayoutsKnown && (
                                <Typography variant="body2" color="text.secondary">Payouts <strong>${detailPayoutTotal.toFixed(2)}</strong></Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No technician assigned yet.</Typography>
                        )}
                      </JobDetailsSection>

                      <JobDetailsSection title="Money">
                        {detailJob?.sourceSystem === 'SMOOBU' && (detailJob?.rentalAmount || selectedEvent.booking?.rentalAmount) && (
                          <InfoRow label="Rental" value={formatCurrency(detailJob?.rentalAmount || selectedEvent.booking?.rentalAmount)} />
                        )}
                        <InfoRow label="Job total" value={formatCurrency(detailJob?.totalAmount)} />
                        {detailPayoutsKnown && <InfoRow label="Payouts" value={`−$${detailPayoutTotal.toFixed(2)}`} />}
                        {detailPayoutsKnown && detailTotal != null && (
                          <Tooltip title="Job total (tax included) minus the technician payouts: what the company keeps" placement="left">
                            <Box>
                              <InfoRow
                                label="Margin"
                                value={(
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: detailTotal - detailPayoutTotal >= 0 ? 'success.main' : 'error.main' }}>
                                    {`${detailTotal - detailPayoutTotal < 0 ? '−' : ''}$${Math.abs(detailTotal - detailPayoutTotal).toFixed(2)}`}
                                  </Typography>
                                )}
                              />
                            </Box>
                          </Tooltip>
                        )}
                        {detailJob?.outstandingBalance > 0 && (
                          <InfoRow label="Outstanding" value={<Typography variant="body2" color="warning.main">{formatCurrency(detailJob.outstandingBalance)}</Typography>} />
                        )}
                      </JobDetailsSection>

                      {/* Property manager — vacation rental jobs */}
                      {detailJob?.sourceSystem === 'SMOOBU' && (
                        <JobDetailsSection title="Property manager">
                          {linkingClient ? (
                            <CircularProgress size={20} />
                          ) : (
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={detailJob.clientProfileId || ''}
                              onChange={(e) => handleLinkJobToClient(e.target.value)}
                              disabled={loadingClients || linkingClient}
                              SelectProps={{ displayEmpty: true }}
                            >
                              <MenuItem value=""><em>Not assigned</em></MenuItem>
                              {clientProfiles.map((client) => (
                                <MenuItem key={client.id} value={client.id}>
                                  {client.User ? `${client.User.firstName} ${client.User.lastName}` : `Client ${client.id.substring(0, 8)}`}
                                  {client.User?.email && ` (${client.User.email})`}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        </JobDetailsSection>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>

        {/* Edit mode: Save / Cancel always reachable */}
        {editingJobDetails && (
          <Box
            sx={{
              position: 'sticky', bottom: 0, bgcolor: 'background.paper',
              borderTop: '1px solid', borderColor: 'divider',
              px: { xs: 1.5, md: 3 }, py: 1.5,
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1,
            }}
          >
            {!detailJob?.bookingId && jobEditForm && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                Customer will be charged <strong>${((Number(jobEditForm.totalAmount) || 0) + extrasTotal(jobEditExtras)).toFixed(2)}</strong>
              </Typography>
            )}
            <Button onClick={handleCancelEditJob} disabled={savingJobDetails}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveJobDetails}
              disabled={savingJobDetails}
              startIcon={savingJobDetails ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {savingJobDetails ? 'Saving...' : 'Save changes'}
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Confirmation Dialog for Removing Individual Maid */}
      <Dialog open={confirmRemoval.open} onClose={() => setConfirmRemoval({ open: false, maidId: null, maidName: '' })}>
        <DialogTitle>Remove Technician Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{confirmRemoval.maidName}</strong> from this job assignment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoval({ open: false, maidId: null, maidName: '' })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemoveMaid} color="error" variant="contained" disabled={assigning}>
            {assigning ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Removing All Assignments */}
      <Dialog open={confirmRemoveAll} onClose={() => setConfirmRemoveAll(false)}>
        <DialogTitle>Remove All Assignments</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove all technician assignments from this job? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveAll(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveAllAssignments} color="error" variant="contained" disabled={assigning}>
            {assigning ? 'Removing...' : 'Remove All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deleting Job */}
      <Dialog 
        open={confirmDeleteJob.open} 
        onClose={() => setConfirmDeleteJob({ open: false, jobId: null, jobTitle: '' })}
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job for <strong>{confirmDeleteJob.jobTitle}</strong>? 
            This action will permanently remove the job and all its associated data. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteJob({ open: false, jobId: null, jobTitle: '' })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteJob} color="error" variant="contained" disabled={assigning}>
            {assigning ? 'Deleting...' : 'Delete Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar drag/resize: confirm the new time and who gets emailed */}
      <Dialog open={Boolean(rescheduleConfirm)} onClose={handleCancelReschedule} maxWidth="xs" fullWidth>
        <DialogTitle>Reschedule job?</DialogTitle>
        {rescheduleConfirm && (() => {
          const fmt = (d) => (d ? d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
          const fmtEnd = (d) => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
          const rc = rescheduleConfirm;
          return (
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{rc.customerName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                {fmt(rc.fromStart)} – {fmtEnd(rc.fromEnd)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                {fmt(rc.toStart)} – {fmtEnd(rc.toEnd)}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rc.canNotifyTechnicians && rc.notifyTechnicians}
                    disabled={!rc.canNotifyTechnicians || rc.saving}
                    onChange={(e) => setRescheduleConfirm({ ...rc, notifyTechnicians: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2">
                    Email the technicians
                    {rc.technicians.length > 0 ? ` (${rc.technicians.join(', ')})` : ' (none assigned)'}
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rc.canNotifyCustomer && rc.notifyCustomer}
                    disabled={!rc.canNotifyCustomer || rc.saving}
                    onChange={(e) => setRescheduleConfirm({ ...rc, notifyCustomer: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2">
                    Email the customer
                    {rc.customerEmail ? ` (${rc.customerEmail})` : ' (no email on file)'}
                  </Typography>
                }
              />
              {rc.notifyBlockedReason && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  No emails can be sent: {rc.notifyBlockedReason}
                </Typography>
              )}
              {!rc.notifyBlockedReason && rc.technicians.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  The customer can only be emailed once a technician is assigned.
                </Typography>
              )}
            </DialogContent>
          );
        })()}
        <DialogActions>
          <Button onClick={handleCancelReschedule} disabled={rescheduleConfirm?.saving}>
            Cancel move
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReschedule}
            disabled={rescheduleConfirm?.saving}
            startIcon={rescheduleConfirm?.saving ? <CircularProgress size={16} /> : null}
          >
            {rescheduleConfirm?.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notify technicians: the standard assignment email + an optional note */}
      <Dialog open={Boolean(notifyDialog)} onClose={() => !notifyingTechnicians && setNotifyDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Notify technicians</DialogTitle>
        <DialogContent>
          {notifyDialog && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Each technician gets the job assignment email: date and time, address, their role and payout,
                the services they do, and a link to the job in their portal.
              </Typography>
              {notifyDialog.recipients.length === 0 ? (
                <Alert severity="warning">No technician is assigned to this job.</Alert>
              ) : (
                <Stack spacing={1}>
                  {notifyDialog.recipients.map((r) => (
                    <Box key={r.id} display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>{r.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}</Avatar>
                      <Box flex={1}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {r.name || 'Technician'}{r.role === 'LEAD' ? ' · Lead' : ''}
                        </Typography>
                        <Typography variant="caption" color={r.email ? 'text.secondary' : 'error'}>
                          {r.email || 'No email address: won\'t be notified'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
              <TextField
                label="Note from the office (optional)"
                placeholder="e.g. Please confirm you can make it"
                multiline
                minRows={3}
                value={notifyDialog.note}
                onChange={(e) => setNotifyDialog((d) => ({ ...d, note: e.target.value }))}
                inputProps={{ maxLength: 2000 }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNotifyDialog(null)} color="inherit" disabled={notifyingTechnicians}>Cancel</Button>
          <Button
            onClick={handleSendNotifications}
            variant="contained"
            disabled={notifyingTechnicians || !notifyDialog?.recipients.some((r) => r.email)}
            startIcon={notifyingTechnicians ? <CircularProgress size={16} /> : <MailIcon />}
          >
            {notifyingTechnicians ? 'Sending...' : `Send to ${notifyDialog?.recipients.filter((r) => r.email).length || 0}`}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageLayout>
  );
};

export default ServiceSchedulePage; 